import React, {useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import ReactDOM from 'react-dom';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import unifiedGitDataService from '../services/UnifiedGitDataService';
import indexedDbFileStorage, {GIT_STATE} from '../utils/indexedDbFileStorage';
import * as vfsClient from '../utils/vfsClient';
import {buildTreeFromFileMap} from '../hooks/useVfsFileState';
import {computeCompaction} from '../utils/compactTree';
import commitMessageStorage from '../utils/commitMessageStorage';
import { setGitRef } from '../utils/gitBranchStorage';
import Modal from './Modal';
import {Button, Dropdown, Segmented, Tooltip} from 'antd';
import {ArrowClockwise, DotsThree, FilePlus, Files, FolderPlus, MagnifyingGlass, UploadSimple, Warning, X} from '@phosphor-icons/react';
import Icon from './icons/Icon';
import HexagonalLoader from './HexagonalLoader';
import {getFileIconName} from './icons/fileIcons';
import MonacoSearchPanel from './MonacoSearchPanel';
import './RepositoryTree.css';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { encodePathSegment, encodeServiceNameForUrl } from '../utils/urlPathSegment';
import { closestPaths } from '../utils/codeRoute';
import { devLog } from '../utils/devLog';
import { forceResyncServiceFromVfs } from '../utils/forceResyncServiceFromVfs';
import TreeUploadDialog from './TreeUploadDialog';
import TreeZipChoice from './TreeZipChoice';
import TreeRenameInput from './tree/TreeRenameInput';
import TreeMoveDialog from './tree/TreeMoveDialog';
import TreeDeleteConfirm from './tree/TreeDeleteConfirm';
import TreeCreateDialog from './tree/TreeCreateDialog';
import { baseName, childPath, movedPath, movePaths, parentOf } from '../utils/treeEntryNames';
import { unpackableArchives } from '../utils/treeUpload/readArchive';
import { collectFromDataTransfer, collectFromFileList, isOsFileDrag } from '../utils/treeUpload/collectEntries';
import { foldersToExpand, joinPath } from '../utils/treeUpload/uploadPlan';
import { bytesToBase64, isBinaryFile as isBinaryName, mimeTypeFor } from '../utils/binaryFiles';
import { fetchIntoCache, reconcileScopeWithVfs } from '../utils/vfsCacheScopes';
import { announceCacheScopesChanged, CACHE_SCOPES_CHANGED_EVENT } from '../utils/indexedDbFileStorage';
import { relativeTo, scopesOverlap } from '../utils/cacheScopePaths';
import {
  FOLDER_PAGE_SIZE, VIRTUALIZE_AT, ancestorFolders, buildRows, childrenIndex, foldersAbove, foldersToRefresh, itemsFromListing,
  mergeFolderListing, searchMatches, unsentPaths, withLocalState,
} from '../utils/lazyTree';
import useProjectPresence from '../hooks/useProjectPresence';
import { useElementWidth } from '../hooks/useMediaQuery';
import useSinceLastSeen from '../hooks/useSinceLastSeen';
import useRepoFlags from '../hooks/useRepoFlags';
import { readOnlyReason, repoOfPath } from '../utils/repoFlags';
import RepoFlagBadges from './repoFlags/RepoFlagBadges';
import { presenceAt } from '../utils/presenceModel';
import { ModifiedMarker, TreePresenceMarker } from './presence/Presence';

// dataTransfer type of an item dragged WITHIN the tree (to move it), as opposed to files dragged in from the OS.
const MOVE_DRAG_TYPE = 'application/x-mia-tree-item';
// A row's height (RepositoryTree.css `.tree-item`), for drawing only the rows on screen in a big tree.
const ROW_HEIGHT = 30;
// One listing in flight: a folder's page.
const listingKey = (folder, cursor) => `${folder}#${cursor}`;

async function eachLimited(items, limit, worker) {
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      await worker(item); // eslint-disable-line no-await-in-loop
    }
  };
  await Promise.all(Array.from({length: Math.min(limit, items.length)}, run));
}

/**
 * Get current tenant ID from localStorage
 * @returns {string|null}
 */
const getTenantId = () => {
  try {
    const stored = localStorage.getItem('mia_selectedTenant');
    if (stored) {
      const tenant = JSON.parse(stored);
      return tenant.tenantId || tenant.id || null;
    }
  } catch (error) {
    console.error('Failed to get tenant ID:', error); /* eslint-disable-line no-console */
  }
  return null;
};


/**
 * RepositoryTree Component - Displays GitLab repository file structure
 * Now responsible for URL synchronization
 */
const RepositoryTree = ({repositoryUrl = null, autoOpenPath = null, autoOpenLine = null, autoOpenStrict = false, onAutoOpenMissing = null, onFileSelect = () => {}, selectedFilePath = null, serviceName = null, projectId: miaProjectId = null, fileErrors = {}, onAskAIToFix = null, isPullingFromGit = false, tenantId = null, branchName = null, vfsBacked = false, syncUrlOnFileSelect = true, forceFreshTree = false, readOnly = false}) => {
  const params = useParams();
  const navigate = useNavigate();
  // The router's location (not window.location): the same in the app, and the one a MemoryRouter holds in tests.
  const location = useLocation();
  // Live presence (utils/presenceClient.js): who else has a file open or is typing in it, and which AI agent writes.
  const presence = useProjectPresence(vfsBacked && !repositoryUrl && miaProjectId ? miaProjectId : null);
  // "M · LB": files others changed since you last left the project (collaboration phase 4).
  const sinceLastSeen = useSinceLastSeen(vfsBacked && !repositoryUrl && miaProjectId ? miaProjectId : null);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  // The tree's own (re)load, apart from `loading` (also set while a file opens): placeholder rows until the first files.
  const [treeLoading, setTreeLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [projectId, setProjectId] = useState(null);
  const [ref, setRef] = useState(null); // Branch/tag - will be fetched from project info
  const [refType, setRefType] = useState('branch'); // Track whether it's a branch or tag
  const [projectInfoFetched, setProjectInfoFetched] = useState(false); // Track if we've fetched project info
  const [contextMenu, setContextMenu] = useState(null);
  // Repos flagged Read-only / No build (utils/repoFlags.js): badges on their folders, no create, delete, upload or move
  // inside a read-only one (vfs-service refuses them anyway).
  const repoFlags = useRepoFlags(miaProjectId);
  const [creating, setCreating] = useState(null); // New file / New folder open: {kind: 'file' | 'folder', folder}
  const [selectedItem, setSelectedItem] = useState(null);
  const [fileStates, setFileStates] = useState({}); // Map of filePath -> gitState
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('files'); // 'files' or 'search'
  const [showProblemsModal, setShowProblemsModal] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null); // Track folder being dragged over
  const [uploadSession, setUploadSession] = useState(null); // {collected, target} while the upload dialog is open
  const [zipChoice, setZipChoice] = useState(null); // {collected, target}: a .zip among them, "Unpack here" or "Keep as a file"
  const [justUploaded, setJustUploaded] = useState(() => new Set()); // tree paths to show selected after an upload
  const [moveError, setMoveError] = useState(null);
  const [renamingPath, setRenamingPath] = useState(null); // the row whose name is being edited (Rename, F2)
  const [movingItem, setMovingItem] = useState(null); // the item of the open Move to… dialog
  const [deleting, setDeleting] = useState(null); // {item, fileCount, busy}: the open delete confirmation
  const pendingFocusRef = useRef(null); // the row to focus once drawn, after a rename or a move
  // A vfs-backed tree loads one folder at a time, when it is opened (utils/lazyTree.js): no download of every file
  // to show the tree. A git-backed tree keeps drawing its whole local cache.
  const lazy = Boolean(vfsBacked && !repositoryUrl);
  // Folders whose children are in the tree ('' = its root): folder → {nextCursor, total}. Mirrored in state to redraw.
  const loadedFoldersRef = useRef(new Map());
  const [loadedFolders, setLoadedFolders] = useState(() => new Map());
  const loadingFoldersRef = useRef(new Set());
  const relistFoldersRef = useRef(new Set());
  const serviceRef = useRef(serviceName);
  serviceRef.current = serviceName;
  const expandedRef = useRef(expandedFolders);
  expandedRef.current = expandedFolders;
  const absorbedRef = useRef(new Set());
  const itemsContainerRef = useRef(null);
  const rowsRef = useRef([]);
  const [scrollWindow, setScrollWindow] = useState({top: 0, height: 900});
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadTargetRef = useRef(''); // folder the file/folder picker uploads into

  const [isDraggingFile, setIsDraggingFile] = useState(false); // Track if dragging files
  const [gitLoading, setGitLoading] = useState(false); // Git operation in progress
  const [gitLoadingMessage, setGitLoadingMessage] = useState(''); // Loading message
  const [gitProgress, setGitProgress] = useState({ current: 0, total: 0 }); // Progress tracking
  const [gitPushFiles, setGitPushFiles] = useState([]); // Files being pushed with their status
  const [gitToolStatus, setGitToolStatus] = useState({ checked: false, toolEnabled: null, authenticated: null, providerName: null }); // Git tool enablement status
  const contextMenuRef = useRef(null);

  // Keep the context menu fully on screen: it opens at the click point and is nudged back inside the viewport
  // when it would overflow the right or bottom edge.
  useLayoutEffect(() => {
    const element = contextMenuRef.current;
    if (!element || !contextMenu) { return; }
    const rect = element.getBoundingClientRect();
    const margin = 8;
    element.style.left = `${Math.max(margin, Math.min(contextMenu.x, window.innerWidth - rect.width - margin))}px`;
    element.style.top = `${Math.max(margin, Math.min(contextMenu.y, window.innerHeight - rect.height - margin))}px`;
  }, [contextMenu]);
  const dragCounterRef = useRef(0); // Track drag enter/leave for nested elements
  const lastNavigatedFileRef = useRef(null); // Track last file we navigated to
  const isPullingRef = useRef(false); // Prevent concurrent pulls and pull loops

  /**
   * Check if the git provider tool is enabled on the backend and if the user is authenticated.
   * Returns { toolEnabled: bool, authenticated: bool, providerName: string }
   */
  const checkGitToolStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    const provider = unifiedGitDataService.detectProvider(repoUrl);
    const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

    try {
      // Check if the tool is enabled on the backend
      let toolEnabled = true; // Optimistic default
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const { default: config } = await import('../config');
        const bffUrl = config.bffUrl || window.location.origin;
        const resolvedTenantId = tenantId || getTenantId();
        const toolsParams = resolvedTenantId ? `?tenantId=${resolvedTenantId}` : '';
        const toolsResponse = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (toolsResponse.ok) {
          const toolsData = await toolsResponse.json();
          const allTools = toolsData?.data?.items || [];
          // Look for tools belonging to this provider category
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            // gitlab (default)
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          // If there are provider tools and ALL of them are disabled → tool is disabled
          if (providerTools.length > 0) {
            toolEnabled = providerTools.some(t => t.enabled !== false);
          }
        }
      } catch (toolsErr) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Could not check tool status, assuming enabled:', toolsErr.message);
        toolEnabled = true;
      }

      // Check authentication status
      const authStatus = await unifiedGitDataService.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Error checking git tool status:', err);
      return { toolEnabled: true, authenticated: false, providerName };
    }
  };

  // Check git tool status whenever the repository URL changes
  useEffect(() => {
    if (!repositoryUrl) return;

    setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });

    let cancelled = false;
    checkGitToolStatus(repositoryUrl).then(result => {
      if (!cancelled) {
        setGitToolStatus({ checked: true, ...result });
        // eslint-disable-next-line no-console
        devLog(`🔧 Git tool status for ${result.providerName}:`, result);
      }
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositoryUrl]);

  // Helper function to update URL with file path
  const updateUrlWithFile = (filePath) => {
    // The Dashboard/Files, embedded Chat Files tab, and /brain adapters
    // (Canvas.jsx, ChatPreviewPanel.jsx, GlobalBrain.jsx) reuse this same
    // component to browse a project-root/brain "virtual service" (a
    // synthetic serviceName like "." or "brain") instead of a real,
    // independently-routable Code-mode service. Navigating the URL's own
    // service segment to that synthetic name on every file click hijacked
    // the page away from its actual identity (e.g. Dashboard's
    // "Project Configuration" pseudo-resource) — live-confirmed: selecting
    // a brain file replaced .../Project%20Configuration/main/preview with
    // .../_root_/main/code/brain%2F..., breaking the URL's meaning entirely.
    // Real Code-mode usage (including a real vfsBacked service with no
    // external repo) still wants this sync — only these three adapters
    // pass syncUrlOnFileSelect={false} to opt out.
    if (!syncUrlOnFileSelect) {
      return;
    }
    if (!tenantId || !miaProjectId || !serviceName || !branchName) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Cannot update URL - missing params:', {
        tenantId: !!tenantId,
        miaProjectId: !!miaProjectId,
        serviceName: !!serviceName,
        branchName: !!branchName,
        filePath: filePath
      });
      return;
    }
    
    // Mark this file as "we just navigated to it" to prevent URL effect from re-loading it
    lastNavigatedFileRef.current = filePath;
    
    const encodedFilePath = encodeURIComponent(filePath);
    const newPath = `/code/${tenantId}/${miaProjectId}/${encodeServiceNameForUrl(serviceName)}/${encodePathSegment(branchName)}/code/${encodedFilePath}`;
    // Selecting a file must not drop the URL's own query string (most
    // importantly `conversationId` — losing it here broke Code mode's
    // "which conversation built this project" binding on the very next
    // page load/refresh, even though the file itself opened fine in the
    // current tab).
    // Already there (a link that opened this file): leave the URL alone, so its `#L42` stays and no duplicate
    // history entry is pushed.
    let currentPath = location.pathname || '';
    try { currentPath = decodeURIComponent(currentPath); } catch { /* keep it encoded */ }
    let decodedNewPath = newPath;
    try { decodedNewPath = decodeURIComponent(newPath); } catch { /* keep it encoded */ }
    if (currentPath.endsWith(decodedNewPath)) { return; }
    const newFullPath = newPath + (location.search || '');
    // eslint-disable-next-line no-console
    devLog('🔗 Updating URL with file:', filePath, '→', newFullPath);
    navigate(newFullPath);
  };

  // Parse repository URL to extract project ID (supports GitLab, GitHub, Azure DevOps)
  useEffect(() => {
    // eslint-disable-next-line no-console
    devLog('🔍 Repository URL parsing effect triggered:', {
      repositoryUrl,
      hasUrl: !!repositoryUrl,
      urlType: typeof repositoryUrl
    });
    
    if (repositoryUrl) {
      try {
        // Detect provider
        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        let extractedProjectId;
        
        // eslint-disable-next-line no-console
        devLog(`🔍 Detected provider: ${provider} for URL:`, repositoryUrl);
        
        if (provider === 'azuredevops') {
          // Azure DevOps format: org/project/_git/repo
          // Or just the path directly: mia-platform-adevops/mia-demo/_git/hello-dotnet
          if (repositoryUrl.includes('://')) {
            const url = new URL(repositoryUrl);
            const pathParts = url.pathname.split('/').filter(Boolean);
            extractedProjectId = pathParts.join('/');
          } else {
            // Direct path format
            extractedProjectId = repositoryUrl;
          }
        } else {
          // GitLab/GitHub format: https://domain.com/namespace/project.git
          const url = new URL(repositoryUrl.replace('.git', ''));
          const pathParts = url.pathname.split('/').filter(Boolean);
          
          if (pathParts.length >= 2) {
            // Use the full path as project ID (GitLab supports nested namespaces)
            extractedProjectId = pathParts.join('/');
          }
        }
        
        if (extractedProjectId) {
          // eslint-disable-next-line no-console
          devLog(`✅ Extracted ${provider} project ID:`, extractedProjectId);
          setProjectId(extractedProjectId);
        } else {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Could not extract project ID from repository URL:', repositoryUrl);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse repository URL:', err, 'URL:', repositoryUrl);
        setError('Invalid repository URL');
      }
    } else {
      // Expected: a vfs project has no repository URL at all, and a git-backed one has not loaded it yet.
      devLog('⏳ Repository URL is not set yet');
    }
  }, [repositoryUrl]);

  // Fetch project info to get default branch (do NOT set ref from branchName until we try this first)
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) {
        // eslint-disable-next-line no-console
        devLog('⏭️ Skipping project info fetch - projectId not set yet');
        return;
      }

      // Guard: verify that projectId (state) was actually derived from the current
      // repositoryUrl (prop). When the user switches resources the two effects fire
      // in separate React renders, so for one render cycle projectId still holds the
      // OLD value while repositoryUrl already has the NEW value → wrong provider +
      // wrong project_id combination → 404.
      // Re-derive the expected projectId from the current URL and skip if they don't match.
      if (repositoryUrl) {
        try {
          const provider = unifiedGitDataService.detectProvider(repositoryUrl);
          let expectedId;
          if (provider === 'azuredevops') {
            if (repositoryUrl.includes('://')) {
              const u = new URL(repositoryUrl);
              expectedId = u.pathname.split('/').filter(Boolean).join('/');
            } else {
              expectedId = repositoryUrl;
            }
          } else {
            const u = new URL(repositoryUrl.replace('.git', ''));
            expectedId = u.pathname.split('/').filter(Boolean).join('/');
          }
          if (expectedId && expectedId !== projectId) {
            // eslint-disable-next-line no-console
            devLog('⏭️ Skipping project info fetch - projectId/repositoryUrl not yet in sync', { projectId, expectedId });
            return;
          }
        } catch (_) {
          // If URL parsing fails here just let the call through; it will fail gracefully
        }
      }

      try {
        // eslint-disable-next-line no-console
        devLog('📡 Fetching project info to get default branch...');
        const info = await unifiedGitDataService.getProject(projectId, repositoryUrl);

        // Use default_branch if available, otherwise fall back to branchName prop or 'main'
        const defaultBranch = info.default_branch || branchName || 'main';
        setRef(defaultBranch);
        setRefType('branch'); // Initially it's always the default branch
        setProjectInfoFetched(true);
        
        // Save default branch to localStorage using utility
        if (miaProjectId && serviceName) {
          setGitRef(miaProjectId, serviceName, defaultBranch);
          // eslint-disable-next-line no-console
          devLog(`💾 Saved default branch to localStorage: ${defaultBranch}`);
        }

        // eslint-disable-next-line no-console
        devLog('📦 Project info fetched. Default branch:', defaultBranch);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch project info:', err);
        // Fall back to branchName prop or 'main' if we can't get project info
        const fallbackBranch = branchName || 'main';
        // eslint-disable-next-line no-console
        devLog('🌿 Using fallback branch after error:', fallbackBranch);
        setRef(fallbackBranch);
        setRefType('branch');
        setProjectInfoFetched(true);

        // Save fallback branch to localStorage using utility
        if (miaProjectId && serviceName) {
          setGitRef(miaProjectId, serviceName, fallbackBranch);
          // eslint-disable-next-line no-console
          devLog(`💾 Saved fallback branch to localStorage: ${fallbackBranch}`);
        }
      }
    };

    fetchProjectInfo();
  }, [projectId, repositoryUrl, branchName]);

  // Load root tree ONLY after project info is fetched and ref is set
  useEffect(() => {
    if (projectId && ref && projectInfoFetched) {
      // eslint-disable-next-line no-console
      devLog('🌳 Loading tree with branch:', ref);
      loadTree('', 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ref, projectInfoFetched]);

  // Refresh tree when switching between services - reload from IndexedDB
  useEffect(() => {
    if (miaProjectId && serviceName) {
      // eslint-disable-next-line no-console
      devLog('🔄 Service changed, refreshing RepositoryTree from IndexedDB:', serviceName);
      // Reset expanded folders and reload tree
      setExpandedFolders(new Set());
      setError(null);
      if (lazy) {
        // Listed from vfs-service one folder at a time: nothing to line up first, and nothing of the previous service.
        loadedFoldersRef.current = new Map();
        setLoadedFolders(new Map());
        setTree([]);
        loadTree('', 0);
        return;
      }

      // BUG FIX (live-reported, "git status on IndexedDB is no longer
      // useful"): loadTree()'s vfs-service fetch only ever fires when
      // IndexedDB has ZERO cached files for this service — once ANY files
      // exist locally (from the very first load), every subsequent call
      // here (e.g. this effect re-firing on a second RepositoryTree mount,
      // such as the one embedded in ChatPreviewPanel's Files tab existing
      // alongside the one in Code mode) trusts that cache forever, with no
      // freshness check at all — unlike MonacoFileEditor's per-file check.
      // vfs-service is the master until the user's first Monaco open (see
      // forceResyncServiceFromVfs's own doc comment for the full design) —
      // reconcile here first so the tree structure honors that same rule,
      // not just individual file content once opened.
      forceResyncServiceFromVfs(miaProjectId, serviceName).finally(() => {
        // Note: loadTree will handle missing projectId/ref gracefully
        // It will either load from IndexedDB or show appropriate error
        loadTree('', 0);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miaProjectId, serviceName]);

  // Fetch file states from IndexedDB
  const fetchFileStatesRef = useRef(() => {});
  useEffect(() => {
    let previous = '';
    const fetchFileStates = async () => {
      if (!miaProjectId || !serviceName) return;

      try {
        const allFiles = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        const statesMap = {};
        
        allFiles.forEach(file => {
          // Use gitState (the new system) exclusively
          statesMap[file.filePath] = file.gitState || GIT_STATE.MODIFIED;
        });
        
        // Unchanged: no new object, so the tree does not redraw every few seconds for nothing.
        const signature = JSON.stringify(statesMap);
        if (signature === previous) { return; }
        previous = signature;
        setFileStates(statesMap);
      } catch (error) {
        console.error('Error fetching file states:', error); /* eslint-disable-line no-console */
      }
    };

    fetchFileStates();
    fetchFileStatesRef.current = fetchFileStates;

    // A write to the local cache (an edit, a sync) refreshes the states right away; the poll is the fallback, slower
    // on a vfs-backed tree, where it is not what keeps the tree in sync.
    let timer = null;
    const soon = () => { clearTimeout(timer); timer = setTimeout(fetchFileStates, 250); };
    window.addEventListener('monacoFileChanged', soon);
    window.addEventListener(CACHE_SCOPES_CHANGED_EVENT, soon);
    const interval = setInterval(fetchFileStates, lazy ? 10000 : 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
      window.removeEventListener('monacoFileChanged', soon);
      window.removeEventListener(CACHE_SCOPES_CHANGED_EVENT, soon);
    };
  }, [miaProjectId, serviceName, lazy]);

  // Another tree on overlapping files (Home › Files vs a service's code view, here or in another tab) changed the
  // cache: line this one up with vfs-service and redraw.
  useEffect(() => {
    if (!miaProjectId || !serviceName || !vfsBacked || repositoryUrl) { return undefined; }
    const onChanged = (detail) => {
      if (!detail || detail.projectId !== miaProjectId) { return; }
      const scopes = detail.scopes || [];
      if (!scopes.includes('*') && !scopes.some((scope) => scopesOverlap(scope, serviceName))) { return; }
      if (lazy) {
        // Only the folders the change is in, when it says which paths.
        const paths = (detail.paths || []).map((path) => relativeTo(serviceName, path)).filter((path) => path !== null);
        if (detail.paths?.length) { refreshTreePaths(paths); } else { refreshOpenFolders(); }
        return;
      }
      reconcileScopeWithVfs(miaProjectId, serviceName, {force: true}).catch(() => null).finally(() => loadTree('', 0));
    };
    const onWindowEvent = (event) => onChanged(event.detail);
    window.addEventListener(CACHE_SCOPES_CHANGED_EVENT, onWindowEvent);
    let channel = null;
    try {
      channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('mia-flow-vfs-cache') : null;
      if (channel) { channel.onmessage = (event) => onChanged(event.data); }
    } catch {
      channel = null;
    }
    return () => {
      window.removeEventListener(CACHE_SCOPES_CHANGED_EVENT, onWindowEvent);
      channel?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miaProjectId, serviceName, vfsBacked, repositoryUrl]);

  // Auto-expand folders to show selected file (a lazy tree then loads them)
  useEffect(() => {
    if (!selectedFilePath || (!lazy && (!projectId || !ref))) return;

    // Get all parent folders that need to be expanded
    const pathParts = selectedFilePath.split('/');
    const foldersToExpand = new Set(expandedFolders);
    
    // Build up the path incrementally
    let currentPath = '';
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentPath += (currentPath ? '/' : '') + pathParts[i];
      foldersToExpand.add(currentPath);
    }
    
    // Only update if there are new folders to expand
    if (foldersToExpand.size > expandedFolders.size) {
      setExpandedFolders(foldersToExpand);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilePath, projectId, ref]);

  // Scroll selected file into view
  useEffect(() => {
    if (!selectedFilePath) return;
    
    // Use setTimeout to ensure DOM has updated after folder expansion
    const timer = setTimeout(() => {
      // A big tree draws only the rows on screen: scroll to the selected one first, so it is drawn.
      const container = itemsContainerRef.current;
      const index = rowsRef.current.findIndex((row) => row.path === selectedFilePath);
      if (container && index >= 0 && rowsRef.current.length > VIRTUALIZE_AT) {
        const top = index * ROW_HEIGHT;
        if (top < container.scrollTop || top + ROW_HEIGHT > container.scrollTop + container.clientHeight) {
          container.scrollTop = Math.max(0, top - container.clientHeight / 2);
        }
      }
      const selectedElement = document.querySelector('.repository-tree-items .tree-item.selected');
      if (selectedElement && typeof selectedElement.scrollIntoView === 'function') {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [selectedFilePath, expandedFolders]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  // Listen for git push events to refresh the tree
  useEffect(() => {
    const handleGitPush = () => {
      // eslint-disable-next-line no-console
      devLog('🔄 Git Push completed - keeping tree visible, just clearing isNew flags...');
      // Don't reset the tree or expanded folders after push
      // The tree should remain visible with the same structure
      // Just reload to update file states (remove isNew badges from pushed files)
      if (miaProjectId && serviceName) {
        loadTree('', 0);
      }
    };

    const handleRepositoryFilesUpdated = (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, fileCount} = event.detail || {};
      
      // Only refresh if it's for this project/service
      if (eventProjectId === miaProjectId && eventServiceName === serviceName) {
        // eslint-disable-next-line no-console
        devLog(`🌲 Repository files updated (${fileCount} files) - refreshing tree view`);
        // Reset expanded folders and reload tree from IndexedDB
        setExpandedFolders(new Set());
        loadTree('', 0);
      }
    };

    const handleGitPull = async (event) => {
      // Check if this pull request is for this specific service
      const {projectId: eventProjectId, serviceName: eventServiceName} = event.detail || {};
      
      // Only process pull requests for this specific service
      if (eventProjectId !== miaProjectId || eventServiceName !== serviceName) {
        // eslint-disable-next-line no-console
        devLog(`⏭️ Skipping pull request - not for this service (this: ${serviceName}, event: ${eventServiceName})`);
        return;
      }

      // ── PRE-FLIGHT: check tool enablement & authentication ──────────────────
      const provider = unifiedGitDataService.detectProvider(repositoryUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // Use already-checked status if available, otherwise run the check now
      let statusToCheck = gitToolStatus;
      if (!statusToCheck.checked) {
        statusToCheck = await checkGitToolStatus(repositoryUrl);
        setGitToolStatus({ checked: true, ...statusToCheck });
      }

      if (statusToCheck.toolEnabled === false) {
        // eslint-disable-next-line no-console
        console.warn(`🚫 Cannot pull: ${providerName} tool is disabled on the server`);
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { success: false, error: `${providerName} tool is disabled`, projectId: miaProjectId, serviceName }
        }));
        return;
      }

      if (statusToCheck.authenticated === false) {
        // eslint-disable-next-line no-console
        console.warn(`🔐 Cannot pull: not authenticated with ${providerName}`);
        const authErrorMsg = `Not authenticated with ${providerName}. Please connect your account in the Tools page.`;
        setError(authErrorMsg);
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: { success: false, error: authErrorMsg, projectId: miaProjectId, serviceName }
        }));
        return;
      }
      // ────────────────────────────────────────────────────────────────────────

      // Use branchName prop as fallback if ref is not set yet
      const effectiveRef = ref || branchName;
      
      if (!projectId || !effectiveRef || !miaProjectId || !serviceName) {
        // eslint-disable-next-line no-console
        console.error('⚠️ Cannot pull: missing required parameters', {
          projectId: projectId || 'MISSING',
          ref: ref || 'MISSING',
          branchName: branchName || 'MISSING',
          effectiveRef: effectiveRef || 'MISSING',
          miaProjectId: miaProjectId || 'MISSING',
          serviceName: serviceName || 'MISSING'
        });
        
        // Dispatch failure event
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: false,
            error: `Missing required parameters`,
            projectId: miaProjectId,
            serviceName
          }
        }));
        return;
      }
      
      try {
        // Show loading state
        setGitLoading(true);
        isPullingRef.current = true; // Set flag to prevent concurrent pulls
        setGitLoadingMessage(`Connecting to ${providerName}...`);
        setGitProgress({ current: 0, total: 0 });
        setError(null);

        setGitLoadingMessage('Fetching repository structure...');
        // Fetch entire tree from Git (auth handled via cookies)
        const items = await unifiedGitDataService.getRepositoryTree(projectId, repositoryUrl, '', effectiveRef);

        const fileItems = items.filter(item => item.type === 'blob');
        const totalFiles = fileItems.length;
        
        // eslint-disable-next-line no-console
        devLog(`📥 Fetched ${totalFiles} files from ${providerName}`);

        setGitProgress({ current: 0, total: totalFiles });
        setGitLoadingMessage(`Downloading files (0/${totalFiles})...`);

        // Save all files to IndexedDB
        let currentFile = 0;
        for (const item of fileItems) {
          try {
            const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, effectiveRef);

            // Detect if file is binary by extension (SVG is text-based, not binary)
            const ext = item.path.split('.').pop()?.toLowerCase();
            const binaryExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'ico', 'webp', 'tiff', 'pdf', 'zip', 'tar', 'gz'];
            const isBinary = binaryExtensions.includes(ext); // SVG explicitly excluded
            
            // For binary files, keep base64 content; for text files, use decoded content
            const contentToStore = isBinary ? fileData.content : (fileData.decodedContent || '');
            
            await indexedDbFileStorage.saveFile(miaProjectId, serviceName, item.path, contentToStore, {
              originalContent: contentToStore,
              fileName: item.name,
              branch: effectiveRef,
              isBinary,
              encoding: isBinary ? 'base64' : 'utf-8',
              fromGit: true // Force ORIGINAL state since this is pulled from GitLab
            });
            
            currentFile++;
            setGitProgress({ current: currentFile, total: totalFiles });
            setGitLoadingMessage(`Downloading files (${currentFile}/${totalFiles})...`);
            
            // eslint-disable-next-line no-console
            devLog(`💾 Saved to IndexedDB: ${item.path} (${currentFile}/${totalFiles})`);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to fetch ${item.path}:`, err);
            currentFile++;
            setGitProgress({ current: currentFile, total: totalFiles });
          }
        }

        // Remove local files that were deleted remotely (only ORIGINAL/PUSHED state - no local changes)
        const remoteFilePaths = new Set(fileItems.map(item => item.path));
        const localFiles = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        for (const localFile of localFiles) {
          const isRemotelyDeleted = !remoteFilePaths.has(localFile.filePath);
          const hasNoLocalChanges = localFile.gitState === 'original' || localFile.gitState === 'pushed';
          if (isRemotelyDeleted && hasNoLocalChanges) {
            await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, localFile.filePath, { suppressEvent: true });
            // eslint-disable-next-line no-console
            devLog(`🗑️ Removed remotely-deleted file from IndexedDB: ${localFile.filePath}`);
          }
        }

        setGitLoadingMessage('Loading files into tree view...');
        
        // eslint-disable-next-line no-console
        devLog('✅ Pull complete, reloading tree from IndexedDB');
        
        // Reset expanded folders and reload the tree from IndexedDB
        setExpandedFolders(new Set());
        await loadTree('', 0);

        // Dispatch event to notify other components (Monaco editor and preview will refresh automatically)
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: true,
            projectId: miaProjectId,
            serviceName,
            branch: effectiveRef
          }
        }));
        
        // Hide loading state
        setGitLoading(false);
        isPullingRef.current = false; // Clear pulling flag
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        
      } catch (err) {
        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';
        // eslint-disable-next-line no-console
        console.error(`Failed to pull from ${providerName}:`, err);
        
        // Check if it's an authentication error
        const isAuthError = err.message && err.message.includes('Not authenticated');
        const errorMessage = isAuthError 
          ? `Not authenticated with ${providerName}. Please connect your account in the Tools page.`
          : `Failed to pull: ${err.message}`;
        
        setError(errorMessage);
        
        isPullingRef.current = false; // Clear pulling flag even on error
        // Dispatch failure event
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {
          detail: {
            success: false,
            error: err.message,
            projectId: miaProjectId,
            serviceName,
            branch: effectiveRef
          }
        }));
        
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
      }
    };

    const handleGitPushRequest = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, files} = event.detail || {};
      
      // Only handle if this event is for this specific service
      if (miaProjectId !== eventProjectId || serviceName !== eventServiceName) {
        return;
      }
      
      // eslint-disable-next-line no-console
      devLog(`🚀 Git Push: Pushing ${files?.length || 0} file(s) to GitLab...`);
      
      if (!projectId || !ref || !miaProjectId || !serviceName) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Cannot push: missing required parameters');
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: false,
            error: 'Missing required parameters'
          }
        }));
        return;
      }
      
      try {
        // Show loading state
        setGitLoading(true);
        setGitLoadingMessage('Preparing to push changes...');
        setGitProgress({ current: 0, total: 0 });
        setError(null);

        const provider = unifiedGitDataService.detectProvider(repositoryUrl);
        const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

        // Get modified and deleted files from IndexedDB
        setGitLoadingMessage('Collecting modified files...');
        const modifiedFilesData = await indexedDbFileStorage.getModifiedFiles(miaProjectId, serviceName);
        const deletedFilesData = await indexedDbFileStorage.getDeletedFiles(miaProjectId, serviceName);
        
        // eslint-disable-next-line no-console
        devLog('📊 Files retrieved from IndexedDB:', {
          modifiedCount: modifiedFilesData.length,
          deletedCount: deletedFilesData.length,
          modifiedFiles: modifiedFilesData.map(f => ({path: f.filePath, state: f.state, contentLength: f.content?.length})),
          deletedFiles: deletedFilesData.map(f => ({path: f.filePath, state: f.state}))
        });
        
        const modifiedFiles = modifiedFilesData.map(f => ({
          path: f.filePath,
          content: f.content
        }));
        const deletedFiles = deletedFilesData.map(f => f.filePath);

        if (modifiedFiles.length === 0 && deletedFiles.length === 0) {
          // eslint-disable-next-line no-console
          devLog('⚠️ No modified or deleted files to push');
          setGitLoading(false);
          setGitLoadingMessage('');
          setGitProgress({ current: 0, total: 0 });
          setGitPushFiles([]);
          window.dispatchEvent(new CustomEvent('gitPushCompleted', {
            detail: {
              success: true,
              filesCount: 0
            }
          }));
          return;
        }

        // Set total for progress tracking
        const totalFiles = modifiedFiles.length + deletedFiles.length;
        setGitProgress({ current: 0, total: totalFiles });
        setGitLoadingMessage(`Preparing ${totalFiles} file(s) for push...`);

        // vfs-backed with NO external repositoryUrl (a project's `brain/` or
        // any other vfs-only folder — no remote configured by design, see
        // today's per-service/per-brain git-boundary work): there is no
        // GitLab/GitHub tree to pull/conflict-check against, and no
        // meaningful "which branch?" question to ask, so skip the entire
        // GitLab-specific pull → conflict-check → action-building pipeline
        // below and commit straight to vfs-service instead, via the exact
        // same write/delete routes ProjectFilesBrowser's instant-save
        // already used successfully. This is what actually PERSISTS an edit
        // for a repo with no remote — without this branch, edits only ever
        // accumulated in the IndexedDB cache and never reached vfs-service.
        if (vfsBacked && !repositoryUrl) {
          setGitLoadingMessage(`Saving ${totalFiles} file(s) to vfs-service...`);
          let processedCount = 0;
          try {
            for (const file of modifiedFiles) {
              const vfsPath = `${serviceName}/${file.path}`.replace(/\/+/g, '/');
              // eslint-disable-next-line no-await-in-loop
              await vfsClient.writeFile(miaProjectId, vfsPath, file.content);
              // eslint-disable-next-line no-await-in-loop
              await indexedDbFileStorage.markAsSent(miaProjectId, serviceName, file.path);
              processedCount += 1;
              setGitProgress({ current: processedCount, total: totalFiles });
              setGitPushFiles(prev => prev.map(f => (f.path === file.path ? {...f, status: 'completed'} : f)));
            }
            for (const filePath of deletedFiles) {
              const vfsPath = `${serviceName}/${filePath}`.replace(/\/+/g, '/');
              // eslint-disable-next-line no-await-in-loop
              await vfsClient.deleteEntry(miaProjectId, vfsPath);
              // eslint-disable-next-line no-await-in-loop
              await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, filePath);
              processedCount += 1;
              setGitProgress({ current: processedCount, total: totalFiles });
              setGitPushFiles(prev => prev.map(f => (f.path === filePath ? {...f, status: 'completed'} : f)));
            }

            await loadTree('', 0);
            setGitLoading(false);
            setGitLoadingMessage('');
            setGitProgress({ current: 0, total: 0 });
            setGitPushFiles([]);
            window.dispatchEvent(new CustomEvent('gitPushCompleted', {
              detail: {
                success: true,
                projectId: miaProjectId,
                serviceName,
                filesCount: totalFiles,
                vfsBacked: true
              }
            }));
          } catch (vfsSaveError) {
            // eslint-disable-next-line no-console
            console.error('❌ vfs-direct save failed:', vfsSaveError);
            setError(vfsSaveError.message || 'Failed to save to vfs-service');
            setGitLoading(false);
            setGitLoadingMessage('');
            window.dispatchEvent(new CustomEvent('gitPushCompleted', {
              detail: { success: false, error: vfsSaveError.message, projectId: miaProjectId, serviceName }
            }));
          }
          return;
        }

        // STEP 1: Pull latest changes from GitLab to sync with remote
        setGitLoadingMessage('🔄 Pulling latest changes from GitLab...');
        // eslint-disable-next-line no-console
        devLog('🔄 Step 1: Pulling latest changes from GitLab before push...');
        
        const gitlabTree = await unifiedGitDataService.getRepositoryTree(projectId, repositoryUrl, '', ref);
        const existingFilesSet = new Set(gitlabTree.filter(item => item.type === 'blob').map(item => item.path));
        
        // eslint-disable-next-line no-console
        devLog(`📋 GitLab has ${existingFilesSet.size} existing files`);
        
        // STEP 2: Fetch content of modified files from GitLab to check for conflicts
        setGitLoadingMessage('🔍 Checking for conflicts...');
        const conflicts = [];
        
        for (const file of modifiedFiles) {
          if (existingFilesSet.has(file.path)) {
            try {
              const gitlabFile = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, file.path, ref);
              const localFile = modifiedFilesData.find(f => f.filePath === file.path);
              
              // Check if GitLab version differs from our original version
              if (localFile?.originalContent && gitlabFile.decodedContent !== localFile.originalContent) {
                conflicts.push({
                  path: file.path,
                  localContent: file.content,
                  remoteContent: gitlabFile.decodedContent,
                  originalContent: localFile.originalContent
                });
              }
            } catch (err) {
              // File might have been deleted remotely, that's ok
              // eslint-disable-next-line no-console
              devLog(`ℹ️ Could not fetch ${file.path} from GitLab (might be deleted):`, err.message);
            }
          }
        }
        
        if (conflicts.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(`⚠️ Found ${conflicts.length} file(s) with conflicts:`, conflicts.map(c => c.path));
          
          // For now, use local version (can be enhanced with merge UI later)
          // eslint-disable-next-line no-console
          devLog('📝 Using local version for conflicted files (remote changes will be overwritten)');
        }
        
        // eslint-disable-next-line no-console
        devLog('✅ Conflict check complete, proceeding with push...');
        
        // eslint-disable-next-line no-console
        devLog(`📋 GitLab has ${existingFilesSet.size} existing files`);

        // Determine correct action based on whether file exists in GitLab
        setGitLoadingMessage('Preparing file actions...');
        
        // Track all file paths to detect duplicates
        const processedPaths = new Set();
        const fileActions = [];
        
        for (const file of modifiedFiles) {
          if (file.content === undefined || file.content === null) continue;
          if (processedPaths.has(file.path)) {
            // eslint-disable-next-line no-console
            console.warn(`⚠️ Skipping duplicate file: ${file.path}`);
            continue;
          }
          
          processedPaths.add(file.path);
          
          // Use correct action: 'create' for new files, 'update' for existing files
          const existsInGitLab = existingFilesSet.has(file.path);
          const action = existsInGitLab ? 'update' : 'create';
          const fileData = modifiedFilesData.find(f => f.filePath === file.path);
          const isBinary = fileData?.isBinary || false;
          
          // eslint-disable-next-line no-console
          devLog(`📝 ${action.toUpperCase()} action for ${file.path} (binary: ${isBinary}, existsInGitLab: ${existsInGitLab})`);
          
          const actionObj = {
            action,
            file_path: file.path,
            content: file.content
          };
          
          // For binary files, specify base64 encoding
          if (isBinary) {
            actionObj.encoding = 'base64';
          }
          
          fileActions.push(actionObj);
        }

        const deleteActions = deletedFiles
          .filter(filePath => {
            if (processedPaths.has(filePath)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ Skipping delete for file already processed: ${filePath}`);
              return false;
            }
            if (!existingFilesSet.has(filePath)) {
              // eslint-disable-next-line no-console
              console.warn(`⚠️ Skipping delete for file that doesn't exist in GitLab: ${filePath}`);
              return false;
            }
            processedPaths.add(filePath);
            return true;
          })
          .map(filePath => ({
            action: 'delete',
            file_path: filePath
          }));

        const actions = [...fileActions, ...deleteActions];
        
        // Build file list for UI display
        const fileListForDisplay = [
          ...modifiedFiles.map(f => ({
            path: f.path,
            action: existingFilesSet.has(f.path) ? 'update' : 'create',
            status: 'pending'
          })),
          ...deletedFiles.map(f => ({
            path: f,
            action: 'delete',
            status: 'pending'
          }))
        ];
        setGitPushFiles(fileListForDisplay);
        
        if (actions.length === 0) {
          // eslint-disable-next-line no-console
          devLog('⚠️ No valid changes to push');
          setGitLoading(false);
          setGitLoadingMessage('');
          setGitProgress({ current: 0, total: 0 });
          setGitPushFiles([]);
          window.dispatchEvent(new CustomEvent('gitPushCompleted', {
            detail: {
              success: true,
              filesCount: 0
            }
          }));
          return;
        }
        
        // eslint-disable-next-line no-console
        devLog('📤 Pushing to GitLab:', {
          totalActions: actions.length,
          creates: actions.filter(a => a.action === 'create').length,
          updates: actions.filter(a => a.action === 'update').length,
          deletes: actions.filter(a => a.action === 'delete').length
        });

        // Show which files are being pushed
        const createCount = actions.filter(a => a.action === 'create').length;
        const updateCount = actions.filter(a => a.action === 'update').length;
        const deleteCount = actions.filter(a => a.action === 'delete').length;
        setGitLoadingMessage(`Pushing ${createCount} new, ${updateCount} updated, ${deleteCount} deleted file(s)...`);

        // Get accumulated commit messages from IndexedDB
        let commitMessage;
        try {
          const tenantId = getTenantId();
          const accumulatedMessage = await commitMessageStorage.getMessage(tenantId, miaProjectId, serviceName);
          
          if (accumulatedMessage) {
            // Use accumulated commit messages
            commitMessage = accumulatedMessage;
            // eslint-disable-next-line no-console
            devLog('📝 Using accumulated commit messages (length:', commitMessage.length, 'chars)');
          } else {
            // Fallback to default commit message
            const timestamp = new Date().toISOString();
            commitMessage = `Update ${modifiedFiles.length + deletedFiles.length} file(s) via Flow\n\n${modifiedFiles.length > 0 ? `Modified files:\n${modifiedFiles.map(f => `- ${f.path}`).join('\n')}\n` : ''}${deletedFiles.length > 0 ? `Deleted files:\n${deletedFiles.map(f => `- ${f}`).join('\n')}\n` : ''}\nCommitted at: ${timestamp}`;
            // eslint-disable-next-line no-console
            devLog('📝 Using default commit message');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to get commit messages, using default:', error);
          const timestamp = new Date().toISOString();
          commitMessage = `Update ${modifiedFiles.length + deletedFiles.length} file(s) via Flow\n\n${modifiedFiles.length > 0 ? `Modified files:\n${modifiedFiles.map(f => `- ${f.path}`).join('\n')}\n` : ''}${deletedFiles.length > 0 ? `Deleted files:\n${deletedFiles.map(f => `- ${f}`).join('\n')}\n` : ''}\nCommitted at: ${timestamp}`;
        }
        
        // GitLab has a limit on commit message size (typically 1MB for API, but let's be conservative)
        // Truncate if too long to avoid 400 Bad Request errors
        const MAX_COMMIT_MESSAGE_LENGTH = 2000; // 2KB limit - GitLab can be picky about long messages
        if (commitMessage.length > MAX_COMMIT_MESSAGE_LENGTH) {
          const originalLength = commitMessage.length;
          const filesSummary = `\n\nUpdated ${modifiedFiles.length} file(s) and deleted ${deletedFiles.length} file(s).`;
          commitMessage = commitMessage.substring(0, MAX_COMMIT_MESSAGE_LENGTH - filesSummary.length - 50) + '\n\n... (message truncated)' + filesSummary;
          // eslint-disable-next-line no-console
          console.warn(`⚠️ Commit message truncated from ${originalLength} to ${commitMessage.length} chars`);
        }

        // Commit all changes in a single commit
        await unifiedGitDataService.commitChanges(
          projectId,
          repositoryUrl,
          ref,
          commitMessage,
          actions
        );
        
        // Update progress as we process files
        setGitLoadingMessage('Updating file states...');
        let processedCount = 0;
        
        // Mark modified files as sent and delete deleted files from IndexedDB
        for (const file of modifiedFiles) {
          await indexedDbFileStorage.markAsSent(miaProjectId, serviceName, file.path);
          processedCount++;
          setGitProgress({ current: processedCount, total: totalFiles });
          setGitLoadingMessage(`Updated ${processedCount}/${totalFiles}: ${file.path}`);
          
          // Update file status in the list
          setGitPushFiles(prev => prev.map(f => 
            f.path === file.path ? {...f, status: 'completed'} : f
          ));
        }
        for (const filePath of deletedFiles) {
          await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, filePath);
          processedCount++;
          setGitProgress({ current: processedCount, total: totalFiles });
          setGitLoadingMessage(`Updated ${processedCount}/${totalFiles}: ${filePath}`);
          
          // Update file status in the list
          setGitPushFiles(prev => prev.map(f => 
            f.path === filePath ? {...f, status: 'completed'} : f
          ));
        }

        // eslint-disable-next-line no-console
        devLog('✅ Push complete, reloading tree from IndexedDB');
        
        // Clear accumulated commit messages after successful push
        try {
          const tenantId = getTenantId();
          await commitMessageStorage.clearMessage(tenantId, miaProjectId, serviceName);
          // eslint-disable-next-line no-console
          devLog('🧹 Cleared accumulated commit messages after successful push');
        } catch (clearError) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Failed to clear commit messages:', clearError);
        }
        
        setGitLoadingMessage('Refreshing file tree...');
        
        // Reload the tree to update file states
        await loadTree('', 0);

        // Hide loading state
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        setGitPushFiles([]);

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: true,
            projectId: miaProjectId,
            serviceName,
            branch: ref,
            filesCount: modifiedFiles.length + deletedFiles.length
          }
        }));
        
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to push to GitLab:', err);
        
        // Hide loading state on error
        setGitLoading(false);
        setGitLoadingMessage('');
        setGitProgress({ current: 0, total: 0 });
        setGitPushFiles([]);
        setError(`Failed to push: ${err.message}`);
        
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('gitPushCompleted', {
          detail: {
            success: false,
            error: err.message
          }
        }));
      }
    };

    const handleFileOperation = async (event) => {
      const {operation, path, content} = event.detail;
      
      if (operation === 'create' && path && content !== undefined) {
        // eslint-disable-next-line no-console
        devLog('✨ AI is creating new file in tree:', path);
        
        // Extract filename
        const pathParts = path.split('/');
        const fileName = pathParts[pathParts.length - 1];
        
        // Create the new file object
        const newFile = {
          path,
          name: fileName,
          content,
          type: 'blob',
          isNew: true
        };
        
        // Save to IndexedDB
        if (miaProjectId && serviceName) {
          await indexedDbFileStorage.saveFile(miaProjectId, serviceName, path, content, {
            originalContent: '',
            fileName,
            isNew: true
          });
          
          // eslint-disable-line no-console
          devLog('💾 Saved new file to IndexedDB:', path);
        }
        
        // Automatically expand all parent folders and update tree incrementally
        // This preserves the existing expanded state while adding the new file
        setExpandedFolders(prev => {
          const next = new Set(prev);
          const parts = path.split('/');
          let currentPath = '';
          
          // Loop through all folders in the path (excluding the file itself)
          for (let i = 0; i < parts.length - 1; i++) {
            currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
            next.add(currentPath);
          }
          return next;
        });
        
        // Instead of reloading the entire tree, add the new file and folders incrementally
        setTree(prevTree => {
          const newTree = [...prevTree];
          const newItems = [];
          const parentPath = pathParts.slice(0, -1).join('/');
          const fileLevel = pathParts.length - 1;
          
          // Check if file already exists
          const fileExists = newTree.some(item => item.path === path);
          
          if (!fileExists) {
            // Add any missing parent folders
            const foldersToAdd = [];
            for (let i = 1; i < pathParts.length; i++) {
              const folderPath = pathParts.slice(0, i).join('/');
              const folderExists = newTree.some(item => item.path === folderPath);
              
              if (!folderExists) {
                const folderParts = folderPath.split('/');
                const folderName = folderParts[folderParts.length - 1];
                const folderLevel = folderParts.length - 1;
                const folderParentPath = folderParts.slice(0, -1).join('/');
                
                foldersToAdd.push({
                  path: folderPath,
                  name: folderName,
                  type: 'tree',
                  level: folderLevel,
                  parent: folderParentPath || undefined
                });
              }
            }
            
            // Add the new file
            newItems.push({
              path,
              name: fileName,
              type: 'blob',
              level: fileLevel,
              parent: parentPath || undefined,
              isNew: true,
              modified: false
            });
            
            // Combine and sort
            const combinedTree = [...newTree, ...foldersToAdd, ...newItems];
            
            // Sort using the same logic as loadTree
            combinedTree.sort((a, b) => {
              const aPath = a.path.split('/');
              const bPath = b.path.split('/');
              const minLength = Math.min(aPath.length, bPath.length);
              
              for (let i = 0; i < minLength; i++) {
                if (aPath[i] !== bPath[i]) {
                  const aIsFolder = i < aPath.length - 1 || a.type === 'tree';
                  const bIsFolder = i < bPath.length - 1 || b.type === 'tree';
                  
                  if (aIsFolder && !bIsFolder) return -1;
                  if (!aIsFolder && bIsFolder) return 1;
                  
                  return aPath[i].localeCompare(bPath[i], undefined, { numeric: true, sensitivity: 'base' });
                }
              }
              
              if (aPath.length !== bPath.length) {
                return aPath.length - bPath.length;
              }
              
              if (a.type !== b.type) {
                return a.type === 'tree' ? -1 : 1;
              }
              
              return 0;
            });
            
            return combinedTree;
          }
          
          return prevTree;
        });
        
        // Auto-open the file in the editor
        onFileSelect(newFile);
        
        // Dispatch file content changed event to trigger preview update
        window.dispatchEvent(new CustomEvent('fileContentChanged', {
          detail: {
            filePath: path,
            content,
            projectId: miaProjectId,
            serviceName
          }
        }));
        
        // eslint-disable-next-line no-console
        devLog('✅ File added to tree and opened in editor:', path);
      } else if (operation === 'delete' && path) {
        // eslint-disable-next-line no-console
        devLog('🗑️ AI is deleting file from tree:', path);
        
        // Mark as deleted in IndexedDB
        if (miaProjectId && serviceName) {
          await indexedDbFileStorage.markAsDeleted(miaProjectId, serviceName, path);
        }
        
        // Remove the file from tree without reloading everything
        setTree(prevTree => prevTree.filter(item => item.path !== path));
      }
    };

    // Handler for when git pull completes (reload tree only if successful)
    const handleGitPullCompleted = (event) => {
      const {success, projectId: eventProjectId, serviceName: eventServiceName} = event?.detail || {};
      
      // Only reload tree if this event is for this service and pull was successful
      if (eventProjectId === miaProjectId && eventServiceName === serviceName && success) {
        loadTree('', 0);
      } else if (lazy && eventProjectId === miaProjectId && eventServiceName === serviceName && success !== false) {
        // A vfs sync of the local cache: one that names its paths also sent 'vfsTreePathsChanged' (listed there);
        // one that does not (a whole resync) lists the open folders again.
        if (!event.detail.paths) { refreshOpenFolders(); }
      }
    };

    // Handler for live vfs writes during an active agent turn — VfsChatPanel
    // dispatches this (debounced ~2s after the last write in a burst) on
    // every successful vfs_write_file/vfs_patch_file tool call, but until
    // now nothing here was listening for it: this tree only ever refreshed
    // on git push/pull events, so a vfs-backed project's file list stayed
    // stale (showing only whatever existed when the tab first loaded) for
    // the entire time an agent was actively writing files, even though the
    // writes themselves succeeded and the live Preview panel (which DOES
    // listen for this event) updated correctly.
    const handlePreviewFilesUpdated = (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName} = event?.detail || {};
      if (eventProjectId === miaProjectId && eventServiceName === serviceName) {
        if (lazy) { refreshOpenFolders(); } else { loadTree('', 0); }
      }
    };

    // Listen for auto-trigger pull events from Canvas
    const handleAutoTriggerGitPull = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, reason} = event.detail || {};
      
      // Only trigger if this event is for this specific service
      if (miaProjectId === eventProjectId && serviceName === eventServiceName) {
        // eslint-disable-next-line no-console
        devLog(`🤖 Auto-triggering Git Pull (reason: ${reason})...`);
        
        // Call the same handleGitPull function with the event
        await handleGitPull(event);
      }
    };

    // Listen for git ref (branch/tag) change events
    const handleGitRefChanged = async (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, ref: newRef, refType: newRefType} = event.detail || {};
      
      // Only trigger if this event is for this specific service
      if (miaProjectId === eventProjectId && serviceName === eventServiceName) {
        // eslint-disable-next-line no-console
        devLog(`🔄 Git ref changed to ${newRefType}: ${newRef}, reloading files...`);
        
        // Update the ref state and refType
        setRef(newRef);
        setRefType(newRefType || 'branch'); // Default to 'branch' if not specified
        
        // Clear the tree and reload from GitLab with the event
        await handleGitPull(event);
      }
    };

    // Handler for opening files programmatically (from modified files list, search, etc.)
    const handleOpenFileEvent = async (event) => {
      const {filePath, projectId: eventProjectId, serviceName: eventServiceName, lineNumber} = event.detail || {};
      
      // Debug logging
      // eslint-disable-next-line no-console
      devLog('🔍 handleOpenFileEvent received:', {filePath, lineNumber, eventProjectId, eventServiceName});
      
      // Only handle if this event is for this specific service (or no specific service provided)
      if (eventProjectId && eventServiceName && (miaProjectId !== eventProjectId || serviceName !== eventServiceName)) {
        return;
      }
      
      if (!filePath) {
        console.warn('⚠️ openFile event missing filePath'); /* eslint-disable-line no-console */
        return;
      }
      
      // eslint-disable-next-line no-console
      devLog('📂 Opening file from programmatic request:', filePath, lineNumber ? `line ${lineNumber}` : '');
      
      // Find the item in the tree
      const findItemInTree = (items, targetPath) => {
        for (const item of items) {
          if (item.path === targetPath) return item;
          if (item.children) {
            const found = findItemInTree(item.children, targetPath);
            if (found) return found;
          }
        }
        return null;
      };
      
      const item = findItemInTree(tree, filePath);
      
      if (item) {
        // Use the existing handleFileClick function to open the file
        await handleFileClick(item, lineNumber);
      } else {
        // File not in tree yet - try to load from IndexedDB and open
        if (miaProjectId && serviceName) {
          const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, filePath);
          if (fileData) {
            // eslint-disable-next-line no-console
            devLog('📂 File found in IndexedDB, opening:', filePath);
            const gitState = fileData.gitState || GIT_STATE.MODIFIED;
            onFileSelect({
              path: filePath,
              name: filePath.split('/').pop(),
              content: fileData.content,
              isNew: gitState === GIT_STATE.NEW,
              modified: gitState !== GIT_STATE.ORIGINAL,
              lineNumber
            });
          } else if (lazy) {
            // Not loaded yet in a tree that lists a folder when it is opened: its folders open, it opens from vfs.
            setExpandedFolders((previous) => new Set([...previous, ...ancestorFolders(filePath)]));
            await handleFileClick({path: filePath, name: filePath.split('/').pop(), type: 'blob'}, lineNumber);
          } else {
            console.warn('⚠️ File not found in tree or IndexedDB:', filePath); /* eslint-disable-line no-console */
          }
        }
      }
    };

    window.addEventListener('gitPushCompleted', handleGitPush);
    window.addEventListener('gitPushRequested', handleGitPushRequest);
    window.addEventListener('repositoryFilesUpdated', handleRepositoryFilesUpdated);
    window.addEventListener('gitPullRequested', handleGitPull);
    window.addEventListener('gitPullCompleted', handleGitPullCompleted);
    window.addEventListener('fileOperation', handleFileOperation);
    window.addEventListener('autoTriggerGitPull', handleAutoTriggerGitPull);
    window.addEventListener('gitRefChanged', handleGitRefChanged);
    window.addEventListener('openFile', handleOpenFileEvent);
    window.addEventListener('openFileFromSearch', handleOpenFileEvent);
    window.addEventListener('previewFilesUpdated', handlePreviewFilesUpdated);

    return () => {
      window.removeEventListener('gitPushCompleted', handleGitPush);
      window.removeEventListener('gitPushRequested', handleGitPushRequest);
      window.removeEventListener('repositoryFilesUpdated', handleRepositoryFilesUpdated);
      window.removeEventListener('gitPullRequested', handleGitPull);
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
      window.removeEventListener('fileOperation', handleFileOperation);
      window.removeEventListener('autoTriggerGitPull', handleAutoTriggerGitPull);
      window.removeEventListener('gitRefChanged', handleGitRefChanged);
      window.removeEventListener('openFile', handleOpenFileEvent);
      window.removeEventListener('openFileFromSearch', handleOpenFileEvent);
      window.removeEventListener('previewFilesUpdated', handlePreviewFilesUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ref, miaProjectId, serviceName, onFileSelect]);

  // The service the shown tree was loaded for: right after a switch to another service, the tree is still the previous
  // service's for a moment, and a link's file looked up in it was "not there anymore".
  const [treeService, setTreeService] = useState(null);

  const markLoaded = (folder, info) => {
    const next = new Map(loadedFoldersRef.current);
    if (info) { next.set(folder, info); } else { next.delete(folder); }
    loadedFoldersRef.current = next;
    setLoadedFolders(next);
  };

  /**
   * Lists one folder ('' = this tree's root) from vfs-service into the tree: its children replace what the tree had
   * for it. `cursor`: the next page of a folder too big for one listing ("Show more").
   */
  const loadFolder = async (folder, {cursor = 0} = {}) => {
    const forService = serviceName;
    const key = listingKey(folder, cursor);
    if (!miaProjectId || !serviceName) { return; }
    if (loadingFoldersRef.current.has(key)) {
      // Already being listed: once more when that ends, so a change made meanwhile is not missed.
      relistFoldersRef.current.add(key);
      return;
    }
    loadingFoldersRef.current.add(key);
    try {
      const listing = await vfsClient.listFolder(miaProjectId, vfsPathFor(folder), {limit: FOLDER_PAGE_SIZE, cursor});
      if (serviceRef.current !== forService) { return; } // another service is shown by now
      // A service's own .mia/ (comments, handoff notes, suggestions) is shown in Flow's panels, not in its tree.
      const items = itemsFromListing(folder, listing.entries, {skip: folder === '' ? ['.mia'] : []});
      setTree((previous) => mergeFolderListing(previous, folder, items, {append: cursor > 0}));
      markLoaded(folder, {nextCursor: listing.nextCursor, total: listing.total, missing: Boolean(listing.missing)});
    } catch (listError) {
      console.warn(`⚠️ Could not list ${folder || 'the root'} from vfs-service:`, listError); /* eslint-disable-line no-console */
      if (!folder) { setError(listError.message || 'Failed to load files'); }
    } finally {
      loadingFoldersRef.current.delete(key);
      if (relistFoldersRef.current.delete(key) && serviceRef.current === forService) { loadFolder(folder, {cursor}); }
    }
  };

  /**
   * Lists again the root and every folder open in the tree, in parallel (six at a time). A folder loaded once and
   * closed since is forgotten: it is listed again when it is opened.
   */
  const refreshOpenFolders = async () => {
    const open = [...loadedFoldersRef.current.keys()].filter((folder) => !folder || expandedRef.current.has(folder) || absorbedRef.current.has(folder));
    const stale = [...loadedFoldersRef.current.keys()].filter((folder) => folder && !open.includes(folder));
    if (stale.length) {
      const next = new Map(loadedFoldersRef.current);
      stale.forEach((folder) => next.delete(folder));
      loadedFoldersRef.current = next;
      setLoadedFolders(next);
    }
    await eachLimited(open.includes('') ? open : ['', ...open], 6, (folder) => loadFolder(folder));
    fetchFileStatesRef.current();
  };

  /** A change at these tree paths: only the loaded folders they are in are listed again. */
  const refreshTreePaths = async (paths) => {
    const folders = foldersToRefresh(paths, (folder) => loadedFoldersRef.current.has(folder));
    await eachLimited(folders, 6, (folder) => loadFolder(folder));
    fetchFileStatesRef.current();
  };

  const loadTree = async (path, level) => {
    const loadingFor = serviceName;
    if (lazy && path === '') {
      if (!miaProjectId || !serviceName) { setTree([]); setTreeLoading(false); return; }
      try {
        setLoading(true);
        setTreeLoading(true);
        setError(null);
        await refreshOpenFolders();
      } finally {
        setLoading(false);
        setTreeLoading(false);
        setTreeService(loadingFor);
      }
      return;
    }
    try {
      setLoading(true);
      setTreeLoading(true);
      setError(null);

      // Dispatch loading event for top loading bar
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: true, message: 'Loading file tree from IndexedDB...'}
      }));

      // eslint-disable-next-line no-console
      devLog(`🌳 loadTree called - path: "${path}", level: ${level}`);

      if (path === '') {
        // Root level - load from localStorage ONLY
        // GitLab fetch should ONLY happen via explicit Pull button
        
        if (!miaProjectId || !serviceName) {
          // eslint-disable-next-line no-console
          console.warn('⚠️ Cannot load tree: missing projectId or serviceName');
          setTree([]);
          return;
        }

        // A vfs-backed tree with a warm cache: line it up with vfs-service first (one listing call; files deleted,
        // moved or added through another tree on the same files — Home › Files vs a service's code view — or by
        // someone else). Files with unsaved local work are kept. A failure keeps showing the cache.
        if (vfsBacked && !repositoryUrl && !forceFreshTree) {
          try {
            await reconcileScopeWithVfs(miaProjectId, serviceName);
          } catch (reconcileError) {
            // eslint-disable-next-line no-console
            console.warn('⚠️ Could not line the tree up with vfs-service; showing the local cache:', reconcileError);
          }
        }

        // Check if we have ANY files in IndexedDB
        const modifiedFiles = await indexedDbFileStorage.getModifiedFiles(miaProjectId, serviceName);
        const allFilesData = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
        const allFiles = allFilesData.map(f => f.filePath);
        
        // eslint-disable-next-line no-console
        devLog(`📦 Loading tree from IndexedDB: ${allFiles.length} total files, ${modifiedFiles.length} modified`);
        
        if ((allFiles.length === 0 || forceFreshTree) && vfsBacked) {
          // vfs-backed service: no git repo to pull from, but a cold
          // IndexedDB cache (fresh browser profile, cleared storage, or the
          // very first Code-tab open right after project creation) still
          // needs SOME source of truth — fetch the real files straight from
          // vfs-service (the same call the Preview pipeline already uses,
          // see Canvas.jsx's vfsClient.fetchServiceFiles call sites). Never
          // attempt a git auto-pull here — that's what previously made this
          // render "Git Integration Disabled" for a project with no git
          // integration involved in the first place.
          //
          // Tree structure is built directly from the vfs-service response
          // (buildTreeFromFileMap, task #68) — no recursive re-read through
          // IndexedDB, which used to be a wasted extra round-trip purely for
          // the tree itself. The IndexedDB write-through is still done (not
          // skippable in this pass — RepositoryTree's own file-content reads
          // for Monaco, e.g. handleFileClick, still depend on it being
          // populated; converting that is separate, larger, deferred scope,
          // see useVfsFileState.js's doc comment / task #114's findings).
          if (isPullingRef.current) {
            return;
          }
          try {
            isPullingRef.current = true;
            // eslint-disable-next-line no-console
            devLog(`📥 Cold IndexedDB cache for vfs-backed service ${serviceName} — fetching from vfs-service`);
            const vfsFiles = await vfsClient.fetchServiceFiles(miaProjectId, serviceName);
            const vfsFilePaths = Object.keys(vfsFiles);
            for (let i = 0; i < vfsFilePaths.length; i += 1) {
              const filePath = vfsFilePaths[i];
              // eslint-disable-next-line no-await-in-loop
              await indexedDbFileStorage.saveFile(miaProjectId, serviceName, filePath, vfsFiles[filePath], {fromVfs: true});
            }
            // eslint-disable-next-line no-console
            devLog(`✅ Populated IndexedDB with ${vfsFilePaths.length} file(s) from vfs-service for ${serviceName}`);
            setTree(buildTreeFromFileMap(vfsFiles));
            // eslint-disable-next-line no-console
            devLog(`✅ Loaded ${vfsFilePaths.length} items directly from vfs-service (no IndexedDB re-read)`);
          } catch (vfsFetchError) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch vfs-backed service files for cold-start tree:', vfsFetchError);
            setTree([]);
          } finally {
            isPullingRef.current = false;
            window.dispatchEvent(new CustomEvent('indexedDBLoading', {
              detail: {loading: false}
            }));
          }
          return;
        }

        if (allFiles.length === 0) {
          // No files in IndexedDB - check if we can trigger automatic Pull
          // Use branchName prop as fallback if ref is not set yet
          const effectiveRef = ref || branchName;

          if (!projectId || !effectiveRef || !miaProjectId || !serviceName) {
            // Missing parameters - if projectId is not set yet, it's likely still being extracted
            // from the repository URL, so don't spam warnings
            if (!projectId && repositoryUrl) {
              // eslint-disable-next-line no-console
              devLog('⏸️ Waiting for project ID to be extracted from repository URL...');
            } else {
              // eslint-disable-next-line no-console
              console.warn('⚠️ Cannot auto-pull: missing required parameters', {
                projectId: projectId || 'MISSING',
                ref: ref || 'MISSING',
                branchName: branchName || 'MISSING',
                effectiveRef: effectiveRef || 'MISSING',
                miaProjectId: miaProjectId || 'MISSING',
                serviceName: serviceName || 'MISSING',
                repositoryUrl: repositoryUrl || 'MISSING'
              });
            }
            setTree([]);
            window.dispatchEvent(new CustomEvent('indexedDBLoading', {
              detail: {loading: false}
            }));
            return;
          }
          
          // Check if authenticated before auto-pulling
          const provider = unifiedGitDataService.detectProvider(repositoryUrl);
          const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

          // Check if already pulling to prevent loops
          if (isPullingRef.current) {
            // eslint-disable-next-line no-console
            devLog('⏭️ Skipping auto-pull - already pulling in progress');
            window.dispatchEvent(new CustomEvent('indexedDBLoading', {
              detail: {loading: false}
            }));
            return;
          }
          
          // eslint-disable-next-line no-console
          devLog(`⚠️ No files in IndexedDB. Automatically pulling from ${providerName}...`);
          
          // Dispatch loading complete event before triggering pull
          window.dispatchEvent(new CustomEvent('indexedDBLoading', {
            detail: {loading: false}
          }));
          
          // Automatically trigger pull via event (since handleGitPull is in a different scope)
          setTree([]);
          window.dispatchEvent(new CustomEvent('gitPullRequested'));
          return;
        }

        // Build tree from localStorage
        const treeItems = [];
        const folders = new Set();

        // Collect all folders from file paths
        for (const filePath of allFiles) {
          const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, filePath);
          
          // Skip deleted files
          const gitState = fileData?.gitState || GIT_STATE.MODIFIED;
          if (fileData && gitState === GIT_STATE.DELETED) continue;

          const pathParts = filePath.split('/');
          
          // Create all parent folders
          for (let i = 1; i < pathParts.length; i++) {
            const folderPath = pathParts.slice(0, i).join('/');
            folders.add(folderPath);
          }
          
          // Add the file
          const fileName = pathParts[pathParts.length - 1];
          const parentPath = pathParts.slice(0, -1).join('/');
          const fileLevel = pathParts.length - 1;
          
          treeItems.push({
            path: filePath,
            name: fileName,
            type: 'blob',
            level: fileLevel,
            parent: parentPath || undefined,
            isNew: gitState === GIT_STATE.NEW,
            modified: gitState === GIT_STATE.MODIFIED,
            deleted: gitState === GIT_STATE.DELETED
          });
        }

        // Add folders
        for (const folderPath of folders) {
          const folderParts = folderPath.split('/');
          const folderName = folderParts[folderParts.length - 1];
          const folderLevel = folderParts.length - 1;
          const parentPath = folderParts.slice(0, -1).join('/');
          
          treeItems.push({
            path: folderPath,
            name: folderName,
            type: 'tree',
            level: folderLevel,
            parent: parentPath || undefined
          });
        }

        // Sort items: hierarchical tree order with folders first, then files, all alphabetically
        treeItems.sort((a, b) => {
          // Split paths to compare hierarchically
          const aPath = a.path.split('/');
          const bPath = b.path.split('/');
          
          // Compare each level of the path
          const minLength = Math.min(aPath.length, bPath.length);
          
          for (let i = 0; i < minLength; i++) {
            // If we're at different folders at this level, sort alphabetically
            if (aPath[i] !== bPath[i]) {
              // Check if both items at this level exist in our tree
              const aIsFolder = i < aPath.length - 1 || a.type === 'tree';
              const bIsFolder = i < bPath.length - 1 || b.type === 'tree';
              
              // If one is a folder and one is a file at this level, folder comes first
              if (aIsFolder && !bIsFolder) return -1;
              if (!aIsFolder && bIsFolder) return 1;
              
              // Both same type at this level, sort alphabetically
              return aPath[i].localeCompare(bPath[i], undefined, { numeric: true, sensitivity: 'base' });
            }
          }
          
          // One path is a prefix of the other
          // Shorter path (parent) comes before longer path (child)
          if (aPath.length !== bPath.length) {
            return aPath.length - bPath.length;
          }
          
          // Same length paths - folders before files
          if (a.type !== b.type) {
            return a.type === 'tree' ? -1 : 1;
          }
          
          return 0;
        });

        // eslint-disable-next-line no-console
        devLog(`✅ Loaded ${treeItems.length} items from IndexedDB`);
        
        setTree(treeItems);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading repository tree:', err);
      setError(err.message || 'Failed to load repository');
    } finally {
      setLoading(false);
      setTreeLoading(false);
      if (path === '') { setTreeService(loadingFor); }
      // Clear loading event
      window.dispatchEvent(new CustomEvent('indexedDBLoading', {
        detail: {loading: false, message: ''}
      }));
    }
  };

  const handleFolderToggle = async (item) => {
    const newExpanded = new Set(expandedFolders);
    
    if (expandedFolders.has(item.path)) {
      // Collapse: just remove from expanded set
      newExpanded.delete(item.path);
    } else {
      // Expand: just add to expanded set
      // All items are already in the tree from initial load
      newExpanded.add(item.path);
    }
    
    setExpandedFolders(newExpanded);
  };

  const handleFileClick = async (item, lineNumber = null) => {
    if (item.type === 'tree') {
      setSelectedItem(item); // the folder "Upload…" and "New File" go into
      await handleFolderToggle(item);
    } else {
      try {
        setLoading(true);
        
        // Check IndexedDB first for new or modified files
        if (miaProjectId && serviceName) {
          let localFile = await indexedDbFileStorage.getFile(miaProjectId, serviceName, item.path);
          if (!localFile && lazy) {
            // A lazy tree caches a file when it is opened: from vfs-service now, then opened as from the cache.
            try {
              await fetchIntoCache(miaProjectId, serviceName, item.path);
              localFile = await indexedDbFileStorage.getFile(miaProjectId, serviceName, item.path);
            } catch (fetchError) {
              console.warn('⚠️ Could not read', item.path, 'from vfs-service:', fetchError); /* eslint-disable-line no-console */
            }
            if (!localFile) {
              // The viewer reads it straight from vfs-service (a PDF, an image, a file the cache could not take).
              onFileSelect({path: item.path, name: item.name, lineNumber});
              updateUrlWithFile(item.path);
              setLoading(false);
              return;
            }
          }
          
          // Check if it's a binary file
          if (localFile?.isBinary) {
            // For binary files, create a download/view experience
            // eslint-disable-next-line no-console
            devLog('📂 Binary file detected:', item.path, `(${localFile.mimeType || 'unknown type'})`);
            
            // Create blob URL for viewing/downloading
            const url = await indexedDbFileStorage.getFileAsUrl(miaProjectId, serviceName, item.path);
            
            if (url) {
              // For images, we can show in editor with special handling
              if (localFile.mimeType?.startsWith('image/')) {
                const gitState = localFile.gitState || GIT_STATE.MODIFIED;
                onFileSelect({
                  path: item.path,
                  name: item.name,
                  content: `[Binary Image File: ${item.name}]\n\nMIME Type: ${localFile.mimeType}\nSize: ${formatFileSize(localFile.size || 0)}\n\nPreview URL: ${url}`,
                  isNew: gitState === GIT_STATE.NEW,
                  modified: gitState !== GIT_STATE.ORIGINAL,
                  isBinary: true,
                  binaryUrl: url,
                  mimeType: localFile.mimeType,
                  lineNumber
                });
                updateUrlWithFile(item.path); // Update URL
              } else {
                // For other binary files (PDFs, etc.), show info and download link
                const gitState = localFile.gitState || GIT_STATE.MODIFIED;
                onFileSelect({
                  path: item.path,
                  name: item.name,
                  content: `[Binary File: ${item.name}]\n\nMIME Type: ${localFile.mimeType || 'application/octet-stream'}\nSize: ${formatFileSize(localFile.size || 0)}\n\nThis file cannot be edited as text.\nDownload URL: ${url}`,
                  isNew: gitState === GIT_STATE.NEW,
                  modified: gitState !== GIT_STATE.ORIGINAL,
                  isBinary: true,
                  binaryUrl: url,
                  mimeType: localFile.mimeType,
                  lineNumber
                });
              }
              setLoading(false);
              return;
            }
          }
          
          // Use IndexedDB if file is new OR has modifications
          const gitState = localFile?.gitState || GIT_STATE.MODIFIED;
          if (localFile && (gitState !== GIT_STATE.ORIGINAL || localFile.content !== undefined)) {
            // Log content type for debugging
            const contentType = typeof localFile.content;
            const contentPreview = localFile.content ? 
              (typeof localFile.content === 'string' ? localFile.content.substring(0, 100) : `[${contentType}]`) : 
              'undefined';
            
            // eslint-disable-next-line no-console
            devLog('📂 Loading file from IndexedDB:', item.path, `(${gitState})`, `type: ${contentType}`, `preview: ${contentPreview}`);
            
            // Validate localStorage content before using it
            const contentLooksInvalid = localFile.content && 
              typeof localFile.content === 'string' &&
              localFile.content.includes('npm install') && 
              localFile.content.includes('yarn add') && 
              !localFile.content.includes('import') &&
              !item.path.endsWith('.md') &&
              !item.path.endsWith('.json');
            
            if (contentLooksInvalid) {
              // eslint-disable-next-line no-console
              console.error('❌ RepositoryTree: Corrupted content in IndexedDB for', item.path);
              // eslint-disable-next-line no-console
              console.error('   Deleting corrupted entry and loading from GitLab instead');
              await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, item.path);
              // Fall through to load from GitLab
            } else {
              // eslint-disable-next-line no-console
              devLog('📂 Loading file from IndexedDB:', item.path, `(${gitState})`);
              
              onFileSelect({
                path: item.path,
                name: item.name,
                content: localFile.content,
                isNew: gitState === GIT_STATE.NEW,
                modified: gitState !== GIT_STATE.ORIGINAL,
                isBinary: localFile.isBinary,
                mimeType: localFile.mimeType,
                lineNumber
              });
              updateUrlWithFile(item.path); // Update URL
              setLoading(false);
              return;
            }
          }
        }
        
        // Fallback to Git provider if not in localStorage
        const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, ref);

        onFileSelect({
          path: item.path,
          name: item.name,
          content: fileData.decodedContent || '',
          size: fileData.size,
          encoding: fileData.encoding,
          lineNumber
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading file:', err);
        setError(`Failed to load file: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Opens the file a link asked for (`?view=files&path=...`) once the tree has loaded: unfolds its folders, then selects
  // it exactly as a click on it would. Once per requested path, so later clicks elsewhere in the tree are never undone.
  const autoOpenedPathRef = useRef(null);
  useEffect(() => {
    const autoOpenKey = `${autoOpenPath}:${autoOpenLine || ''}`;
    if (!autoOpenPath || loading || tree.length === 0 || treeService !== serviceName || autoOpenedPathRef.current === autoOpenKey) { return; }
    // The URL only followed a click in this tree (the file is already open): nothing to open again.
    if (!autoOpenLine && (selectedFilePath === autoOpenPath || lastNavigatedFileRef.current === autoOpenPath)) {
      autoOpenedPathRef.current = autoOpenKey;
      return;
    }
    if (lazy) {
      // Its folders are listed first (opened in the tree): this runs again as each one arrives.
      const parents = ancestorFolders(autoOpenPath);
      if (parents.some((folder) => !loadedFoldersRef.current.has(folder))) {
        setExpandedFolders((previous) => (parents.every((folder) => previous.has(folder)) ? previous : new Set([...previous, ...parents])));
        return;
      }
    }
    const listed = tree.find((candidate) => candidate.type === 'blob' && candidate.path === autoOpenPath);
    // A Code-tab link (`.../code/<file>`): the tree has just been lined up with vfs-service, so a file it does not list
    // is not there anymore (renamed, moved, deleted). Said so, with the closest files, instead of an empty editor.
    if (!listed && autoOpenStrict) {
      autoOpenedPathRef.current = autoOpenKey;
      if (onAutoOpenMissing) {
        const files = tree.filter((candidate) => candidate.type === 'blob' && !candidate.deleted).map((candidate) => candidate.path);
        onAutoOpenMissing({ path: autoOpenPath, matches: closestPaths(autoOpenPath, files) });
      }
      return;
    }
    // A file the cached tree does not list yet (added after the cache was filled: an upload, a result the assistant just
    // wrote) is opened all the same: the viewer reads a PDF, an image or a text file straight from vfs-service.
    const item = listed || (vfsBacked ? { name: autoOpenPath.split('/').pop(), path: autoOpenPath, type: 'blob' } : null);
    if (!item) { return; }
    autoOpenedPathRef.current = autoOpenKey;
    const parts = autoOpenPath.split('/');
    const parents = parts.slice(0, -1).map((_, index) => parts.slice(0, index + 1).join('/'));
    setExpandedFolders((previous) => new Set([...previous, ...parents]));
    // `&line=` (a search result): the editor scrolls to it.
    handleFileClick(item, autoOpenLine || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenPath, autoOpenLine, tree, loading, treeService, loadedFolders]);

  // A lazy tree: every open folder not listed yet is listed now (a click on it, a link or an upload that unfolds a
  // path, a file selected elsewhere). Folders of one path load in parallel: each shows once the one above it is there.
  useEffect(() => {
    if (!lazy || treeService !== serviceName) { return; }
    for (const folder of expandedFolders) {
      if (!loadedFoldersRef.current.has(folder) && !loadingFoldersRef.current.has(listingKey(folder, 0))) { loadFolder(folder); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazy, expandedFolders, treeService, serviceName]);

  // A lazy tree hears which paths changed on vfs-service (Canvas's live sync: a file event, or a command's changed
  // paths) and lists only their folders again, instead of reloading everything.
  useEffect(() => {
    if (!lazy) { return undefined; }
    const onPathsChanged = (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName, paths} = event.detail || {};
      if (eventProjectId !== miaProjectId || eventServiceName !== serviceName) { return; }
      if (Array.isArray(paths)) { refreshTreePaths(paths); } else { refreshOpenFolders(); }
    };
    window.addEventListener('vfsTreePathsChanged', onPathsChanged);
    return () => window.removeEventListener('vfsTreePathsChanged', onPathsChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazy, miaProjectId, serviceName]);

  const handleFileDoubleClick = async (item) => {
    if (item.type === 'tree') {
      return; // Don't open folders in new tab
    }
    
    try {
      setLoading(true);
      const fileData = await unifiedGitDataService.getFileContent(projectId, repositoryUrl, item.path, ref);

      // Open file in new browser tab/window
      const fileContent = fileData.decodedContent || '';
      const blob = new Blob([fileContent], {type: 'text/plain'});
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      
      // Set title of the new window
      if (newWindow) {
        newWindow.document.title = item.name;
        // Clean up the object URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading file:', err);
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Reset expanded folders and reload the tree
    setExpandedFolders(new Set());
    loadTree('', 0);
  };

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      item
    });
    setSelectedItem(item);
  };

  // New file / New folder (toolbar, "⋯", a folder's menu): the dialog (TreeCreateDialog) creates it in the folder last
  // clicked (a folder's menu selects that folder), else at the root of this tree.
  const handleCreateFile = () => {
    setContextMenu(null);
    setCreating({kind: 'file', folder: createTarget()});
  };

  const handleCreateFolder = () => {
    setContextMenu(null);
    setCreating({kind: 'folder', folder: createTarget()});
  };

  // What is at a tree path now, from the rows listed: 'file', 'folder' or null (the dialog's "already there").
  const entryAtTreePath = (path) => {
    const entry = tree.find((candidate) => candidate.path === path && !candidate.deleted && candidate.type !== 'more');
    if (!entry) { return null; }
    return entry.type === 'tree' ? 'folder' : 'file';
  };

  // Creates `name` (a "sub/dir/name.js" path too: the missing folders come with it) in `folder`, then shows it: a file
  // opens in the editor, a folder is opened and selected; the tree is listed again. On a vfs-backed tree it is created
  // on vfs-service (an empty file, or POST /mkdir); else a file is a new file of the local cache, sent with the next push.
  const createEntry = async ({kind, folder}, name) => {
    const fullPath = childPath(folder, name);
    if (createsOnVfs) {
      const vfsPath = vfsPathFor(fullPath);
      // The tree lists only the folders opened: vfs-service says whether it is there already (a file is never replaced).
      if ((await vfsClient.existingPaths(miaProjectId, [vfsPath])).has(vfsPath)) {
        throw new Error(`"${name}" is already there.`);
      }
      if (kind === 'folder') {
        await vfsClient.mkdir(miaProjectId, vfsPath);
      } else {
        await vfsClient.writeFile(miaProjectId, vfsPath, '');
      }
      announceCacheScopesChanged({projectId: miaProjectId, scopes: ['*'], paths: [vfsPath]});
    } else {
      await indexedDbFileStorage.saveFile(miaProjectId, serviceName, fullPath, '', {
        originalContent: '',
        fileName: baseName(fullPath),
        isNew: true
      });
    }
    expandFolders([kind === 'folder' ? fullPath : parentOf(fullPath)].filter(Boolean));
    await loadTree('', 0);
    setCreating(null);
    if (kind === 'folder') {
      setSelectedItem({path: fullPath, name: baseName(fullPath), type: 'tree'});
      pendingFocusRef.current = fullPath;
      devLog('📁 Created folder:', fullPath);
    } else {
      pendingFocusRef.current = fullPath;
      await handleFileClick({path: fullPath, name: baseName(fullPath), type: 'blob'});
      devLog('📄 Created file:', fullPath);
    }
  };

  // Drag and drop: files and folders from the desktop are uploaded; an item dragged inside the tree is moved.
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;

    if (isOsFileDrag(e.dataTransfer) && canUpload) {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;

    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingFile(false);
      setDragOverFolder(null);
    }
  };

  const parentFolderOf = (path) => (path && path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    const isMove = e.dataTransfer.types.includes(MOVE_DRAG_TYPE);
    const isUpload = !isMove && isOsFileDrag(e.dataTransfer);
    if (isUpload && !canUpload) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    if (isMove || isUpload) {
      e.dataTransfer.dropEffect = isMove ? 'move' : 'copy';

      // Highlight the folder the drop goes into: the folder row itself, or a file row's folder.
      if (item && item.type === 'tree') {
        setDragOverFolder(item.path);
      } else if (item) {
        setDragOverFolder(parentFolderOf(item.path) || null);
      } else {
        setDragOverFolder(null);
      }
    }
  };

  // vfs-service path of a tree path: a service's files live under `<serviceName>/`, except at the project root.
  const vfsPathFor = (relativePath) => {
    if (!serviceName || serviceName === '.') { return relativePath || '.'; }
    return relativePath ? `${serviceName}/${relativePath}` : serviceName;
  };

  // Why a tree path (a folder or a file) cannot be changed: it is in a read-only repo; null when it can.
  const lockedReason = (treePath) => readOnlyReason(repoFlags.repos, vfsPathFor(treePath || ''));
  // The flags of a folder that IS a repo (`services/x`, `context/x`), for its badges; null otherwise.
  const repoRootFlags = (item) => {
    if (item.type !== 'tree') { return null; }
    const path = vfsPathFor(item.path);
    return repoOfPath(path) === path ? repoFlags.repos[path] || null : null;
  };
  // Where New file / New folder create: the folder last clicked (a folder's menu selects it), else the root.
  const createTarget = () => (selectedItem?.type === 'tree' ? selectedItem.path : '');
  const createLock = lockedReason(createTarget());
  // Where that is, in words: "in services/app/src", "in the service's root".
  const createLocation = (folder) => {
    if (folder) { return `in ${vfsPathFor(folder)}`; }
    return !serviceName || serviceName === '.' ? "in the project's root" : "in the service's root";
  };

  const canMoveItems = Boolean(!readOnly && vfsBacked && !repositoryUrl && miaProjectId && serviceName && vfsClient.isVfsBackedProject(miaProjectId));
  // Uploads go to vfs-service (one archive, one commit) on a vfs-backed tree; a git-backed tree keeps them in the
  // local cache until its next push. A viewer of a shared project cannot upload.
  const canUpload = Boolean(!readOnly && miaProjectId && serviceName);
  const uploadsToVfs = canMoveItems;
  // New file / New folder: not for a viewer. On vfs-service when the tree is vfs-backed; a git-backed tree keeps a new
  // file in its local cache until the next push, and has no New folder (an empty folder is not kept there: a file
  // named "folder/name.js" makes one).
  const createsOnVfs = canMoveItems;
  const canCreate = Boolean(!readOnly && miaProjectId && serviceName);
  const canCreateFolder = canCreate && createsOnVfs;
  const readOnlyHint = 'You can only view this project';

  const handleItemDragStart = (e, item) => {
    if (!canMoveItems || item.deleted || lockedReason(item.path)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(MOVE_DRAG_TYPE, item.path);
    e.dataTransfer.setData('text/plain', item.path);
    e.dataTransfer.effectAllowed = 'move';
  };

  const expandFolders = (folders) => {
    if (!folders.length) { return; }
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      for (const folder of folders) {
        let current = '';
        for (const part of folder.split('/')) {
          current = current ? `${current}/${part}` : part;
          next.add(current);
        }
      }
      return next;
    });
  };

  // Moves or renames a file or a folder on vfs-service (one commit, git sees a rename), then follows it: the tree, the
  // folders open under it, the file open in the editor (and with it the URL), the selection. vfs-service refuses a
  // destination that exists: the error is thrown, for the caller to say.
  const relocateItem = async (fromPath, toPath) => {
    await vfsClient.moveEntry(miaProjectId, vfsPathFor(fromPath), vfsPathFor(toPath));
    if (lazy) {
      // The cache drops what it had at the old path (unsent work stays); the new one is cached when it is opened.
      for (const record of await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName)) {
        const under = record.filePath === fromPath || record.filePath.startsWith(`${fromPath}/`);
        if (under && ![GIT_STATE.NEW, GIT_STATE.MODIFIED].includes(record.gitState)) {
          await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, record.filePath); // eslint-disable-line no-await-in-loop
        }
      }
    } else {
      // Reconcile the local cache with vfs-service (drops the old path, picks up the new one), then redraw.
      await forceResyncServiceFromVfs(miaProjectId, serviceName, {forceRefresh: true});
    }
    setExpandedFolders((previous) => movePaths(previous, fromPath, toPath));
    expandFolders([parentOf(toPath)].filter(Boolean));
    announceCacheScopesChanged({projectId: miaProjectId, scopes: ['*'], paths: [vfsPathFor(fromPath), vfsPathFor(toPath)]});
    await loadTree('', 0);
    const openNow = movedPath(selectedFilePath, fromPath, toPath);
    if (openNow) { handleFileClick({path: openNow, name: baseName(openNow), type: 'blob'}); }
    setSelectedItem((previous) => {
      const path = movedPath(previous?.path, fromPath, toPath);
      return path ? {...previous, path, name: baseName(path)} : previous;
    });
    pendingFocusRef.current = toPath;
    devLog(`📦 Moved ${fromPath} -> ${toPath}`);
  };

  // Why `fromPath` cannot go into `targetFolder` ('' = project/service root), '' when it is already there, null when it can.
  const moveProblem = (fromPath, targetFolder) => {
    const name = baseName(fromPath);
    if (parentOf(fromPath) === targetFolder) { return ''; }
    if (targetFolder === fromPath || targetFolder.startsWith(`${fromPath}/`)) { return `Cannot move "${name}" into itself`; }
    const toPath = childPath(targetFolder, name);
    if (tree.some((entry) => entry.path === toPath && !entry.deleted)) { return `"${toPath}" already exists. Rename or remove it first.`; }
    return null;
  };

  // Move a file or folder into `targetFolder` ('' = project/service root): a drop in the tree.
  const moveItemTo = async (fromPath, targetFolder) => {
    const problem = moveProblem(fromPath, targetFolder);
    if (problem !== null) {
      if (problem) { setMoveError(problem); }
      return;
    }
    try {
      setMoveError(null);
      await relocateItem(fromPath, childPath(targetFolder, baseName(fromPath)));
    } catch (error) {
      console.error('Failed to move item:', error); /* eslint-disable-line no-console */
      setMoveError(`Could not move "${baseName(fromPath)}": ${error.message}`);
    }
  };

  // Rename (the row's name edited in place): same folder, new name.
  const renameItem = async (item, newName) => {
    setRenamingPath(null);
    try {
      setMoveError(null);
      await relocateItem(item.path, childPath(parentOf(item.path), newName));
    } catch (error) {
      console.error('Failed to rename item:', error); /* eslint-disable-line no-console */
      setMoveError(`Could not rename "${item.name}": ${error.message}`);
      pendingFocusRef.current = item.path;
    }
  };

  // Rename and Move to… are vfs-service moves: not for a viewer, a read-only repo, or a file marked for deletion.
  const canRelocate = (item) => Boolean(item && canMoveItems && !item.deleted && !lockedReason(item.path));
  const siblingNames = (item) => tree
    .filter((entry) => !entry.deleted && entry.type !== 'more' && parentOf(entry.path) === parentOf(item.path))
    .map((entry) => entry.name);
  const startRename = (item) => {
    setContextMenu(null);
    if (canRelocate(item)) { setRenamingPath(item.path); }
  };
  const handleRowKeyDown = (event, item) => {
    const isSelected = item.type === 'tree' ? selectedItem?.path === item.path : selectedFilePath === item.path;
    // F2 renames the row; Enter renames it once it is the one selected (the first Enter opens it).
    if (event.key === 'F2' || (event.key === 'Enter' && isSelected)) {
      if (!canRelocate(item)) { return; }
      event.preventDefault();
      startRename(item);
    }
  };
  // Move to…: every folder of this tree from vfs-service (the dialog first shows the folders already listed).
  const fetchMoveFolders = useCallback(async () => {
    const base = !serviceName || serviceName === '.' ? '.' : serviceName;
    const root = await vfsClient.readTree(miaProjectId, base, 6);
    const found = [];
    const walk = (node, prefix) => {
      for (const child of node?.children || []) {
        if (child.type !== 'directory') { continue; }
        const path = prefix ? `${prefix}/${child.name}` : child.name;
        if ((!prefix && ['.mia', '.git'].includes(child.name)) || vfsClient.isSyncExcludedPath(`${path}/x`)) { continue; }
        found.push(path);
        walk(child, path);
      }
    };
    walk(root, '');
    return found;
  }, [miaProjectId, serviceName]);

  // Git-backed tree: files go into the local cache as new (pushed with the next push), every subfolder kept.
  const uploadToLocalCache = async (collected, targetFolder) => {
    const added = [];
    for (const {file, relPath} of collected.files) {
      try {
        const filePath = joinPath(targetFolder, relPath);
        const content = await readFileContent(file);
        const isBinary = isBinaryFile(file.name, file.type);
        await indexedDbFileStorage.saveFile(miaProjectId, serviceName, filePath, content, {
          originalContent: content,
          fileName: file.name,
          isBinary,
          mimeType: file.type,
          size: file.size,
          isNew: true,
        });
        added.push(filePath);
      } catch (error) {
        console.error(`Failed to upload ${relPath}:`, error); /* eslint-disable-line no-console */
        setMoveError(`Could not upload ${relPath}: ${error.message}`);
      }
    }
    expandFolders(foldersToExpand(added));
    setJustUploaded(new Set(added));
    await loadTree('', 0);
  };

  /** Opens the upload dialog (vfs-backed) or stores the files locally (git-backed). */
  const beginUpload = async (collected, targetFolder) => {
    if (!canUpload) {
      setMoveError(`${readOnlyHint}: uploading is not allowed.`);
      return;
    }
    if (lockedReason(targetFolder)) {
      setMoveError(lockedReason(targetFolder));
      return;
    }
    if (!collected.files.length && !collected.unreadable?.length) { return; }
    if (uploadsToVfs && unpackableArchives(collected).length) {
      setZipChoice({collected, target: targetFolder});
    } else if (uploadsToVfs) {
      setUploadSession({collected, target: targetFolder});
    } else {
      await uploadToLocalCache(collected, targetFolder);
    }
  };

  /** Text as text, anything else (or text that is not UTF-8) as base64: the cache copy of an uploaded file. */
  const cacheContentOf = (bytes, name, type) => {
    if (!isBinaryName(name, type)) {
      try {
        return {content: new TextDecoder('utf-8', {fatal: true}).decode(bytes), isBinary: false};
      } catch {
        // not UTF-8: kept as bytes
      }
    }
    return {content: bytesToBase64(bytes), isBinary: true, mimeType: type || mimeTypeFor(name)};
  };

  /**
   * After an upload: the new files go into the local cache from the bytes already read (no download back), the other
   * trees on the same files are told, and this tree unfolds and selects what arrived. One file is also opened.
   */
  const handleUploadFinished = async ({results, bytesByPath, targetFolder, files}) => {
    const arrived = [];
    for (const file of files) {
      const line = results[file.relPath];
      if (!line || !['created', 'overwritten', 'renamed'].includes(line.status)) { continue; }
      const treePath = joinPath(targetFolder, line.path || file.relPath);
      const bytes = bytesByPath.get(file.relPath);
      if (bytes) {
        const name = treePath.split('/').pop();
        const cached = cacheContentOf(bytes, name, file.file?.type);
        // eslint-disable-next-line no-await-in-loop
        await indexedDbFileStorage.saveFile(miaProjectId, serviceName, treePath, cached.content, {
          fromVfs: true,
          isBinary: cached.isBinary,
          mimeType: cached.mimeType || file.file?.type || undefined,
          size: bytes.length,
          fileName: name,
        });
      }
      arrived.push(treePath);
    }
    // Anything the cache could not take from local bytes (and a cancelled send that may have landed) comes from vfs.
    // A lazy tree lists the folders instead, and caches a file when it is opened.
    if (!lazy) { await reconcileScopeWithVfs(miaProjectId, serviceName, {force: true}).catch(() => null); }
    announceCacheScopesChanged({projectId: miaProjectId, scopes: ['*'], paths: arrived.map(vfsPathFor)});
    expandFolders([...foldersToExpand(arrived), ...(targetFolder ? [targetFolder] : [])]);
    setJustUploaded(new Set(arrived));
    await loadTree('', 0);
    setTimeout(() => {
      document.querySelector('.repository-tree-items .tree-item.just-uploaded')?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }, 150);
    if (arrived.length === 1) {
      const path = arrived[0];
      handleFileClick({path, name: path.split('/').pop(), type: 'blob'});
    }
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    setDragOverFolder(null);

    // Determine target folder: a folder row, or the row's parent folder when a file row is the target
    const targetFolder = targetItem?.type === 'tree' ? targetItem.path : parentFolderOf(targetItem?.path);

    // An item dragged inside the tree: move it
    const movedPath = e.dataTransfer.getData(MOVE_DRAG_TYPE);
    const lockedTarget = lockedReason(targetFolder);
    if (movedPath) {
      if (lockedTarget || lockedReason(movedPath)) { setMoveError(lockedTarget || lockedReason(movedPath)); return; }
      if (canMoveItems) { moveItemTo(movedPath, targetFolder); }
      return;
    }
    if (!isOsFileDrag(e.dataTransfer)) { return; }
    if (!canUpload) {
      setMoveError(`${readOnlyHint}: uploading is not allowed.`);
      return;
    }
    if (lockedTarget) {
      setMoveError(lockedTarget);
      return;
    }
    // Read the dropped entries now: they are only readable during the drop event itself.
    collectFromDataTransfer(e.dataTransfer)
      .then((collected) => beginUpload(collected, targetFolder))
      .catch((error) => setMoveError(`Could not read what was dropped: ${error.message}`));
  };

  // Toolbar / folder menu: "Upload files…" and "Upload folder…" (every subfolder kept).
  // The folder last clicked, else the open file's folder, else the root of this tree.
  const defaultUploadTarget = () => (selectedItem?.type === 'tree' ? selectedItem.path : parentFolderOf(selectedFilePath || ''));
  const pickFiles = (kind, targetFolder) => {
    uploadTargetRef.current = targetFolder;
    setContextMenu(null);
    (kind === 'folder' ? folderInputRef : fileInputRef).current?.click();
  };
  const handleFilesPicked = (e) => {
    const collected = collectFromFileList(e.target.files);
    e.target.value = ''; // allow picking the same files again later
    if (collected.files.length === 0) { return; }
    beginUpload(collected, uploadTargetRef.current);
  };
  const openUploadMenu = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setContextMenu({x: rect.left, y: rect.bottom + 4, item: null, kind: 'upload', target: defaultUploadTarget()});
  };
  const folderChoices = [...new Set(tree.filter((entry) => entry.type === 'tree' && !entry.deleted).map((entry) => entry.path))]
    .sort((a, b) => a.localeCompare(b));

  // Helper function to read file content (supports both text and binary files)
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const isBinary = isBinaryFile(file.name, file.type);
        
        if (isBinary) {
          // For binary files, convert ArrayBuffer to base64
          const arrayBuffer = event.target.result;
          const base64 = arrayBufferToBase64(arrayBuffer);
          resolve(base64);
        } else {
          // For text files, return as string
          resolve(event.target.result);
        }
      };
      
      reader.onerror = () => reject(reader.error);
      
      // Read as appropriate type
      const isBinary = isBinaryFile(file.name, file.type);
      if (isBinary) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  // Helper to determine if a file is binary based on extension or MIME type
  const isBinaryFile = (fileName, mimeType) => {
    // Image files
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.svg', '.webp', '.tiff', '.tif'];
    // Document files
    const docExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    // Archive files
    const archiveExtensions = ['.zip', '.tar', '.gz', '.rar', '.7z'];
    // Media files
    const mediaExtensions = ['.mp3', '.mp4', '.avi', '.mov', '.wav', '.flac', '.ogg'];
    // Binary executables and libraries
    const binaryExtensions = ['.exe', '.dll', '.so', '.dylib', '.bin', '.wasm'];
    // Font files
    const fontExtensions = ['.ttf', '.otf', '.woff', '.woff2', '.eot'];
    
    const allBinaryExtensions = [
      ...imageExtensions,
      ...docExtensions,
      ...archiveExtensions,
      ...mediaExtensions,
      ...binaryExtensions,
      ...fontExtensions
    ];
    
    // Check by extension
    const lowerFileName = fileName.toLowerCase();
    if (allBinaryExtensions.some(ext => lowerFileName.endsWith(ext))) {
      return true;
    }
    
    // Check by MIME type
    if (mimeType) {
      if (mimeType.startsWith('image/') ||
          mimeType.startsWith('audio/') ||
          mimeType.startsWith('video/') ||
          mimeType === 'application/pdf' ||
          mimeType === 'application/zip' ||
          mimeType === 'application/octet-stream' ||
          mimeType.includes('font')) {
        return true;
      }
    }
    
    return false;
  };

  // Helper to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // vfs-backed tree: a delete happens on vfs-service right away (one commit, restorable from History), like every
  // other edit there, and leaves the local cache of EVERY tree on these files (Home › Files and the service's view).
  const deleteOnVfs = async (item) => {
    const isFolder = item.type === 'tree';
    try {
      setMoveError(null);
      await vfsClient.deleteEntry(miaProjectId, vfsPathFor(item.path), {recursive: isFolder}).catch((error) => {
        if (!/\(404\)/.test(error.message || '')) { throw error; } // never saved there: only the local copy goes
      });
      const local = await indexedDbFileStorage.getAllFiles(miaProjectId, serviceName);
      for (const record of local) {
        if (record.filePath === item.path || record.filePath.startsWith(`${item.path}/`)) {
          // eslint-disable-next-line no-await-in-loop
          await indexedDbFileStorage.deleteFile(miaProjectId, serviceName, record.filePath);
        }
      }
      announceCacheScopesChanged({projectId: miaProjectId, scopes: ['*'], paths: [vfsPathFor(item.path)]});
      await loadTree('', 0);
    } catch (error) {
      console.error('Failed to delete:', error); /* eslint-disable-line no-console */
      setMoveError(`Could not delete "${item.name}": ${error.message}`);
    }
  };

  // The files of the local cache under a folder (a git-backed tree draws its whole cache).
  const localFilesUnder = (item) => tree.filter((entry) => entry.type === 'blob' && entry.path.startsWith(`${item.path}/`));

  // Git-backed tree: the files are marked for deletion in the local cache and go with the next push.
  const markDeletedLocally = async (item) => {
    const files = item.type === 'tree' ? localFilesUnder(item) : [item];
    for (const fileItem of files) {
      await indexedDbFileStorage.markAsDeleted(miaProjectId, serviceName, fileItem.path); // eslint-disable-line no-await-in-loop
    }
    await loadTree('', 0);
    devLog(`🗑️ Marked ${files.length} file(s) for deletion:`, item.path);
  };

  // Delete in the menu: the app's own confirmation (TreeDeleteConfirm), which says how many files a folder holds.
  const handleDeleteItem = () => {
    if (!contextMenu || !miaProjectId || !serviceName) return;
    const item = contextMenu.item;
    setContextMenu(null);
    const isFolder = item.type === 'tree';
    if (!canMoveItems && isFolder && localFilesUnder(item).length === 0) {
      devLog('📁 Empty folder - no action needed:', item.path); // folders are derived from the files there
      return;
    }
    if (!isFolder || !lazy) {
      setDeleting({item, fileCount: isFolder ? localFilesUnder(item).length : 1, busy: false});
      return;
    }
    // A lazy tree has listed only the folders opened: vfs-service counts the files of this one.
    setDeleting({item, fileCount: null, busy: false});
    vfsClient.listFilePaths(miaProjectId, {path: vfsPathFor(item.path)})
      .then((paths) => paths.length, () => -1)
      .then((fileCount) => setDeleting((previous) => (previous?.item.path === item.path ? {...previous, fileCount} : previous)));
  };

  const confirmDelete = async () => {
    const {item} = deleting;
    setDeleting((previous) => ({...previous, busy: true}));
    if (canMoveItems) { await deleteOnVfs(item); } else { await markDeletedLocally(item); }
    setDeleting(null);
  };

  // Check if file needs to be pushed to GitLab
  const needsGitPush = (filePath) => {
    const gitState = fileStates[filePath];
    return gitState === GIT_STATE.MODIFIED || gitState === GIT_STATE.NEW || gitState === GIT_STATE.DELETED;
  };

  // Folders with a modified file somewhere under them, and with a file with errors: one lookup per folder row.
  const modifiedFolders = useMemo(() => foldersAbove(unsentPaths(fileStates)), [fileStates]);
  const errorFolders = useMemo(() => foldersAbove(Object.keys(fileErrors || {}).filter((filePath) => fileErrors[filePath]?.length > 0)), [fileErrors]);

  // Check if folder contains any modified files (recursively)
  const folderHasModifications = (folderPath) => modifiedFolders.has(folderPath);

  // Check if file has errors
  const hasErrors = (filePath) => {
    return fileErrors && fileErrors[filePath] && fileErrors[filePath].length > 0;
  };

  // Check if folder contains any files with errors (recursively)
  const folderHasErrors = (folderPath) => errorFolders.has(folderPath);

  // Handle showing problems modal
  const handleShowProblems = () => {
    setShowProblemsModal(true);
  };

  // Get count of files with problems
  const getProblemsCount = () => {
    return Object.keys(fileErrors || {}).length;
  };

  // Get files with errors for display
  const getFilesWithProblems = () => {
    if (!fileErrors) return [];
    
    return Object.entries(fileErrors).map(([filePath, errors]) => ({
      filePath,
      errors,
      errorCount: errors.length
    }));
  };

  // Handle "Ask AI to Fix" button
  const handleAskAIToFix = async (e, item) => {
    e.stopPropagation();
    
    if (!onAskAIToFix || !miaProjectId || !serviceName) return;
    
    try {
      // Get file content from IndexedDB
      const fileData = await indexedDbFileStorage.getFile(miaProjectId, serviceName, item.path);
      
      if (fileData && fileData.content) {
        const errors = fileErrors[item.path] || [];
        const errorDetails = errors.map(err => 
          `Line ${err.startLineNumber}: ${err.message}`
        ).join('\n');
        
        onAskAIToFix(item.path, fileData.content, errorDetails);
      }
    } catch (error) {
      console.error('Error loading file for AI fix:', error);
    }
  };

  const getFileIcon = (item) => {
    if (item.type === 'tree') {
      return expandedFolders.has(item.path) ? 'folder-open' : 'folder';
    }
    
    return getFileIconName(item.name);
  };
  
  const getChevronIcon = (item) => {
    if (item.type !== 'tree') return null;
    return expandedFolders.has(item.path) ? 'chevron-down' : 'chevron-right';
  };
  
  // The rows drawn, worked out once per change instead of on every render: the tree with this browser's unsent work
  // on top (a lazy tree), compact folder chains (not while searching, so every match keeps its real place), then a
  // walk through the open folders, or the matches of the file-name search and the folders above them.
  const shownTree = useMemo(
    () => (lazy ? withLocalState(tree, fileStates, (folder) => loadedFolders.has(folder)) : tree),
    [lazy, tree, fileStates, loadedFolders],
  );
  const compaction = useMemo(() => (searchQuery ? null : computeCompaction(shownTree)), [shownTree, searchQuery]);
  absorbedRef.current = compaction?.absorbed || new Set();
  const treeIndex = useMemo(() => childrenIndex(shownTree), [shownTree]);
  const rows = useMemo(() => {
    if (searchQuery) { return buildRows(treeIndex, {include: searchMatches(shownTree, searchQuery)}); }
    // A folder too big for one listing: a "Show more" row after what is loaded of it.
    const more = new Map();
    if (lazy) {
      for (const [folder, info] of loadedFolders) {
        if (info?.nextCursor) { more.set(folder, {cursor: info.nextCursor, remaining: Math.max(0, (info.total || 0) - info.nextCursor)}); }
      }
    }
    return buildRows(treeIndex, {expanded: expandedFolders, absorbed: compaction?.absorbed, more});
  }, [treeIndex, shownTree, searchQuery, expandedFolders, compaction, lazy, loadedFolders]);
  rowsRef.current = rows;

  // After a rename or a move (or a rename given up), the keyboard focus goes to the item's row once it is drawn.
  useEffect(() => {
    const path = pendingFocusRef.current;
    if (!path || renamingPath) { return; }
    const row = [...(itemsContainerRef.current?.querySelectorAll('[data-tree-path]') || [])].find((element) => element.dataset.treePath === path);
    if (row) {
      pendingFocusRef.current = null;
      row.focus();
      row.scrollIntoView?.({block: 'nearest'});
    }
  });

  // Over VIRTUALIZE_AT rows only those on screen (and a margin) are drawn, between two spacers of the others' height.
  const virtualized = rows.length > VIRTUALIZE_AT;
  const firstRow = virtualized ? Math.max(0, Math.floor(scrollWindow.top / ROW_HEIGHT) - 30) : 0;
  const lastRow = virtualized ? Math.min(rows.length, Math.ceil((scrollWindow.top + scrollWindow.height) / ROW_HEIGHT) + 30) : rows.length;
  const handleItemsScroll = (event) => {
    if (!virtualized) { return; }
    const {scrollTop, clientHeight} = event.currentTarget;
    setScrollWindow({top: scrollTop, height: clientHeight || 900});
  };

  // The explorer's header row: below ~300px Files | Search shows its icons only, below ~240px New folder, Upload and
  // Refresh fold into "⋯" (New file and Problems stay).
  const headerRowRef = useRef(null);
  const moreButtonRef = useRef(null);
  const headerRowWidth = useElementWidth(headerRowRef);
  const headerIconsOnly = headerRowWidth > 0 && headerRowWidth < 300;
  const headerFolded = headerRowWidth > 0 && headerRowWidth < 240;

  const handleSearchInFiles = () => {
    setActiveTab('search');
  };
  
  const handleShowFiles = () => {
    setActiveTab('files');
    setSearchQuery(''); // Clear search query when switching to files
  };

  if (!repositoryUrl && !vfsBacked) {
    return (
      <div className="repository-tree-empty">
        <span className="empty-icon">{'📦'}</span>
        <p>{'No GitLab repository linked to this microservice'}</p>
      </div>
    );
  }

  // Show tool-checking spinner while we verify enablement/auth. Not
  // applicable to a vfs-backed service — there's no git tool to check, and
  // the effect that ever sets gitToolStatus.checked bails out early when
  // there's no repositoryUrl, so this would otherwise spin forever.
  if (!vfsBacked && !gitToolStatus.checked) {
    return (
      <div className="repository-tree-loading">
        <HexagonalLoader
          message="Checking integration status..."
          detailMessage="Verifying your Git provider settings"
        />
      </div>
    );
  }

  // ── BLOCKING: tool is disabled on the server ─────────────────────────────
  if (gitToolStatus.checked && gitToolStatus.toolEnabled === false) {
    const { providerName } = gitToolStatus;
    const providerIconName = providerName === 'GitHub' ? 'github' : providerName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
    return (
      <div className="git-tool-disabled">
        <div className="git-tool-disabled-icon">
          <Icon name={providerIconName} size={48} glow={true} />
        </div>
        <h3 className="git-tool-disabled-title">{providerName} Integration Disabled</h3>
        <p className="git-tool-disabled-desc">
          The <strong>{providerName}</strong> integration is currently disabled. You need to
          enable it in the <strong>Tools</strong> page before you can browse or download
          this repository.
        </p>
        <div className="git-tool-disabled-steps">
          <div className="git-tool-disabled-step">
            <span className="step-number">1</span>
            <span>Go to <strong>Tools</strong> in your settings</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">2</span>
            <span>Enable the <strong>{providerName}</strong> integration</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">3</span>
            <span>Come back and reload this page</span>
          </div>
        </div>
        <div className="git-tool-disabled-actions">
          <button
            className="git-tool-disabled-btn primary"
            onClick={() => { window.location.href = '/tools'; }}
            type="button"
          >
            {'🔧 Go to Tools'}
          </button>
          <button
            className="git-tool-disabled-btn secondary"
            onClick={() => {
              setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });
              checkGitToolStatus(repositoryUrl).then(result => {
                setGitToolStatus({ checked: true, ...result });
              });
            }}
            type="button"
          >
            {'🔄 Recheck Status'}
          </button>
        </div>
      </div>
    );
  }

  // ── BLOCKING: tool enabled but user is not authenticated ─────────────────
  if (gitToolStatus.checked && gitToolStatus.authenticated === false) {
    const { providerName } = gitToolStatus;
    const providerIconName = providerName === 'GitHub' ? 'github' : providerName === 'Azure DevOps' ? 'azure-devops' : 'gitlab';
    return (
      <div className="git-tool-disabled git-tool-not-connected">
        <div className="git-tool-disabled-icon">
          <Icon name={providerIconName} size={48} glow={true} />
        </div>
        <h3 className="git-tool-disabled-title">{providerName} Account Not Connected</h3>
        <p className="git-tool-disabled-desc">
          Your <strong>{providerName}</strong> account is not connected yet. Connect your
          account to browse and download this repository.
        </p>
        <div className="git-tool-disabled-steps">
          <div className="git-tool-disabled-step">
            <span className="step-number">1</span>
            <span>Go to <strong>Tools</strong> in your settings</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">2</span>
            <span>Connect your <strong>{providerName}</strong> account</span>
          </div>
          <div className="git-tool-disabled-step">
            <span className="step-number">3</span>
            <span>Come back here — the repository will load automatically</span>
          </div>
        </div>
        <div className="git-tool-disabled-actions">
          <button
            className="git-tool-disabled-btn primary"
            onClick={() => { window.location.href = '/tools'; }}
            type="button"
          >
            {'🔑 Connect Account'}
          </button>
          <button
            className="git-tool-disabled-btn secondary"
            onClick={() => {
              setGitToolStatus({ checked: false, toolEnabled: null, authenticated: null, providerName: null });
              checkGitToolStatus(repositoryUrl).then(result => {
                setGitToolStatus({ checked: true, ...result });
              });
            }}
            type="button"
          >
            {'🔄 Recheck Status'}
          </button>
        </div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  if (error) {
    // If pulling from Git, show loader instead of error
    if (isPullingFromGit) {
      return (
        <div className="repository-tree-loading">
          <HexagonalLoader 
            message="Pulling from Git..." 
            progress={gitProgress}
            detailMessage={gitLoadingMessage}
          />
        </div>
      );
    }
    
    // Check if it's an authentication error
    const isAuthError = error.includes('Not authenticated');
    const provider = unifiedGitDataService.detectProvider(repositoryUrl);
    const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';
    
    return (
      <div className="repository-tree-error">
        <span className="error-icon">{'⚠️'}</span>
        <p>{error}</p>
        {isAuthError && (
          <p style={{fontSize: '13px', marginTop: '8px', color: '#666'}}>
            Please connect your {providerName} account in the Tools page
          </p>
        )}
        <button
          className="retry-btn"
          onClick={() => {
            if (isAuthError) {
              window.location.href = '/tools';
            } else {
              loadTree('', 0);
            }
          }}
          type="button"
        >
          {isAuthError ? 'Go to Tools' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <>
      
      <div className="repository-tree">
        {/* Git Loading Overlay */}
        {gitLoading && (
          <div className="git-loading-overlay">
            <div className="git-loading-content">
              <div className="git-loading-spinner">
                <Icon name="loader" size={32} />
              </div>
              <div className="git-loading-message">{gitLoadingMessage}</div>
              {gitProgress.total > 0 && (
                <>
                  <div className="git-loading-progress-bar">
                    <div 
                      className="git-loading-progress-fill"
                      style={{ width: `${(gitProgress.current / gitProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="git-loading-progress-text">
                    {gitProgress.current} / {gitProgress.total} files
                  </div>
                </>
              )}
              {gitPushFiles.length > 0 && (
                <div className="git-push-files-list">
                  <div className="git-push-files-header">Files being pushed:</div>
                  <div className="git-push-files-items">
                    {gitPushFiles.map((file, idx) => (
                      <div key={idx} className={`git-push-file-item ${file.status}`}>
                        <div className="git-push-file-icon">
                          {file.status === 'completed' ? '✓' : '○'}
                        </div>
                        <div className="git-push-file-action">
                          {file.action === 'create' && <span className="action-badge create">NEW</span>}
                          {file.action === 'update' && <span className="action-badge update">MOD</span>}
                          {file.action === 'delete' && <span className="action-badge delete">DEL</span>}
                        </div>
                        <div className="git-push-file-path" title={file.path}>
                          {file.path}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="repository-tree-header">
          <div className={`repository-tree-actions${headerIconsOnly ? ' is-icons-only' : ''}`} ref={headerRowRef}>
            <Segmented
              aria-label="Explorer view"
              className="tree-view-switch"
              onChange={(value) => (value === 'files' ? handleShowFiles() : handleSearchInFiles())}
              options={[
                {value: 'files', title: 'Files view', label: <span aria-label={headerIconsOnly ? 'Files' : undefined} className="tree-view-switch-label"><Files aria-hidden size={14} />{!headerIconsOnly && 'Files'}</span>},
                {value: 'search', title: 'Search in files', label: <span aria-label={headerIconsOnly ? 'Search' : undefined} className="tree-view-switch-label"><MagnifyingGlass aria-hidden size={14} />{!headerIconsOnly && 'Search'}</span>},
              ]}
              size="small"
              value={activeTab}
            />
            <div className="header-actions">
              {canCreate && (
                <Tooltip title={createLock || 'New file'}>
                  <Button aria-label="Create new file" className="action-icon-btn" disabled={Boolean(createLock)} icon={<FilePlus size={16} />} onClick={handleCreateFile} size="small" type="text" />
                </Tooltip>
              )}
              {!headerFolded && canCreateFolder && (
                <Tooltip title={createLock || 'New folder'}>
                  <Button aria-label="Create new folder" className="action-icon-btn" disabled={Boolean(createLock)} icon={<FolderPlus size={16} />} onClick={handleCreateFolder} size="small" type="text" />
                </Tooltip>
              )}
              {/* Problems: the files the editor reported errors in (Monaco markers, lifted up by CodeViewWithLogger). */}
              <Tooltip title={getProblemsCount() > 0 ? `Problems: ${getProblemsCount()} file${getProblemsCount() === 1 ? '' : 's'} with errors` : undefined}>
                <Button
                  aria-label={`View all files with problems (${getProblemsCount()})`}
                  className={`action-icon-btn ${getProblemsCount() > 0 ? 'has-problems' : ''}`}
                  disabled={getProblemsCount() === 0}
                  icon={<Warning size={16} />}
                  onClick={handleShowProblems}
                  size="small"
                  title={getProblemsCount() > 0 ? undefined : 'Problems: no files with errors'}
                  type="text"
                >
                  {getProblemsCount() > 0 && (
                    <span className="problems-badge">{getProblemsCount()}</span>
                  )}
                </Button>
              </Tooltip>
              {!headerFolded && (
                <>
                {/* Disabled (a viewer), the reason stays on the button itself as its title. */}
                <Tooltip title={canUpload ? 'Upload files or a folder (or drop them on the tree)' : undefined}>
                  <Button
                    aria-haspopup="menu"
                    aria-label="Upload"
                    className="action-icon-btn tree-upload-trigger"
                    data-testid="tree-upload-menu"
                    disabled={!canUpload}
                    icon={<UploadSimple size={16} />}
                    onClick={openUploadMenu}
                    size="small"
                    title={canUpload ? undefined : readOnlyHint}
                    type="text"
                  />
                </Tooltip>
                </>
              )}
              <input
                data-testid="tree-upload-files-input"
                multiple
                onChange={handleFilesPicked}
                ref={fileInputRef}
                style={{display: 'none'}}
                type="file"
              />
              <input
                data-testid="tree-upload-folder-input"
                multiple
                onChange={handleFilesPicked}
                ref={folderInputRef}
                style={{display: 'none'}}
                type="file"
                webkitdirectory=""
              />
              {headerFolded ? (
                <Dropdown
                  menu={{
                    items: [
                      ...(canCreateFolder ? [{key: 'folder', label: 'Create new folder', icon: <FolderPlus size={14} />, disabled: Boolean(createLock)}] : []),
                      {key: 'upload', label: 'Upload', icon: <UploadSimple size={14} />, disabled: !canUpload, title: canUpload ? undefined : readOnlyHint},
                      {key: 'refresh', label: 'Refresh file tree', icon: <ArrowClockwise size={14} />, disabled: loading},
                    ],
                    onClick: ({key}) => {
                      if (key === 'folder') { handleCreateFolder(); }
                      // The upload menu opens under the "⋯" button, as it does under the Upload button.
                      if (key === 'upload' && moreButtonRef.current) { openUploadMenu({currentTarget: moreButtonRef.current}); }
                      if (key === 'refresh') { handleRefresh(); }
                    },
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button aria-label="More file actions" className="action-icon-btn" data-testid="tree-more-actions" icon={<DotsThree size={16} weight="bold" />} ref={moreButtonRef} size="small" type="text" />
                </Dropdown>
              ) : (
                <Tooltip title="Refresh file tree">
                  <Button aria-label="Refresh file tree" className="action-icon-btn" disabled={loading} icon={<ArrowClockwise size={16} />} onClick={handleRefresh} size="small" type="text" />
                </Tooltip>
              )}
            </div>
          </div>
        
          {activeTab === 'files' ? (
            <>
              <div className="search-container">
                <MagnifyingGlass aria-hidden className="search-container-icon" size={14} />
                <input
                  aria-label="Search files"
                  type="text"
                  className="search-input"
                  placeholder="Search files…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    aria-label="Clear search"
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                    type="button"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
        
              {moveError && (
                <div className="tree-inline-error" role="alert">
                  <span>{moveError}</span>
                  <button aria-label="Dismiss" onClick={() => setMoveError(null)} type="button">×</button>
                </div>
              )}
              <div 
            className={`repository-tree-items ${isDraggingFile ? 'drag-over' : ''}`}
            data-virtualized={virtualized ? 'true' : undefined}
            onScroll={handleItemsScroll}
            ref={itemsContainerRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => handleDragOver(e, null)}
            onDrop={(e) => handleDrop(e, null)}
          >
            {isDraggingFile && (
              <div className="drag-overlay" data-testid="tree-drop-hint">
                <div className="drag-overlay-content">
                  <Icon name="upload" size={14} />
                  <span className="drag-overlay-text">Drop to upload into <strong>{dragOverFolder || (serviceName && serviceName !== '.' ? serviceName : 'the project root')}</strong></span>
                </div>
              </div>
            )}
            {treeLoading && tree.length === 0 && !searchQuery && (
              <div aria-busy="true" aria-label="Loading files" className="tree-skeleton" data-testid="tree-skeleton" role="status">
                {[72, 55, 64, 48, 60, 40].map((width, i) => (
                  <div className="tree-skeleton-row" key={i} style={{paddingLeft: i > 0 && i < 3 ? 22 : 6}}>
                    <span className="tree-skeleton-icon" />
                    <span className="tree-skeleton-name" style={{width: `${width}%`}} />
                  </div>
                ))}
              </div>
            )}
            {virtualized && firstRow > 0 && <div aria-hidden className="tree-rows-spacer" style={{height: firstRow * ROW_HEIGHT}} />}
            {rows.slice(firstRow, lastRow).map((item) => {
                if (item.type === 'more') {
                  const indent = compaction ? compaction.displayLevel({...item, path: `${item.folder}/x`}) : item.level;
                  return (
                    <button
                      className="tree-item tree-item-more"
                      data-testid="tree-show-more"
                      key={item.path}
                      onClick={() => loadFolder(item.folder, {cursor: item.cursor})}
                      style={{paddingLeft: `${8 + indent * 16 + 18}px`}}
                      type="button"
                    >
                      <span className="tree-name">{`Show ${item.remaining ? `${item.remaining} ` : ''}more…`}</span>
                    </button>
                  );
                }

                const itemHasModifications = item.type === 'blob' ? needsGitPush(item.path) : folderHasModifications(item.path);
                const itemHasErrors = item.type === 'blob' ? hasErrors(item.path) : folderHasErrors(item.path);

                if (renamingPath === item.path) {
                  return (
                    <div
                      className={`tree-item renaming ${selectedFilePath === item.path ? 'selected' : ''}`}
                      data-testid="tree-renaming-row"
                      key={item.path}
                      style={{paddingLeft: `${8 + (compaction ? compaction.displayLevel(item) : item.level) * 16}px`}}
                    >
                      {getChevronIcon(item) && (
                        <span className="tree-chevron">
                          <Icon name={getChevronIcon(item)} size={12} />
                        </span>
                      )}
                      <span className="tree-icon">
                        <Icon name={getFileIcon(item)} size={14} />
                      </span>
                      <TreeRenameInput
                        name={item.name}
                        onCancel={() => { pendingFocusRef.current = item.path; setRenamingPath(null); }}
                        onCommit={(newName) => renameItem(item, newName)}
                        siblings={siblingNames(item)}
                      />
                    </div>
                  );
                }

                return (
            <button
              className={`tree-item ${(item.type === 'tree' ? selectedItem?.path === item.path : selectedFilePath === item.path) ? 'selected' : ''} ${item.deleted ? 'deleted' : ''} ${item.isNew ? 'new' : ''} ${itemHasModifications ? 'modified-unpushed' : ''} ${itemHasErrors ? 'has-errors' : ''} ${dragOverFolder === item.path ? 'drag-over-folder' : ''} ${justUploaded.has(item.path) ? 'just-uploaded' : ''} ${item.heavy ? 'heavy' : ''}`}
              key={item.path}
              onClick={() => { if (justUploaded.size) { setJustUploaded(new Set()); } handleFileClick(item); }}
              onContextMenu={(e) => handleContextMenu(e, item)}
              onDoubleClick={() => handleFileDoubleClick(item)}
              onKeyDown={(e) => handleRowKeyDown(e, item)}
              data-tree-path={item.path}
              draggable={canMoveItems && !item.deleted && !lockedReason(item.path)}
              onDragStart={(e) => handleItemDragStart(e, item)}
              onDragEnd={() => setDragOverFolder(null)}
              onDragOver={(e) => handleDragOver(e, item)}
              onDrop={(e) => handleDrop(e, item)}
              style={{paddingLeft: `${8 + (compaction ? compaction.displayLevel(item) : item.level) * 16}px`}}
              title={item.deleted ? 'Marked for deletion' : item.isNew ? 'New (not yet committed)' : itemHasErrors && item.type === 'blob' ? 'Has errors - click Fix with AI' : itemHasErrors && item.type === 'tree' ? 'Contains files with errors' : itemHasModifications && item.type === 'blob' ? 'Modified (not pushed to GitLab)' : itemHasModifications && item.type === 'tree' ? 'Contains modified files' : (item.type === 'blob' ? `Right-click for options | Double-click to open in new tab${canMoveItems ? ' | Drag onto a folder to move it' : ''}` : (canMoveItems ? 'Drop files or drag items here to put them in this folder' : (canUpload ? 'Drag files here to upload to this folder' : 'Folder')))}
              type="button"
            >
            {getChevronIcon(item) && (
              <span className="tree-chevron">
                <Icon name={getChevronIcon(item)} size={12} />
              </span>
            )}
            <span className="tree-icon">
              <Icon name={getFileIcon(item)} size={14} />
            </span>
            <span className="tree-name">{compaction ? compaction.displayName(item) : item.name}</span>
            <RepoFlagBadges compact flags={repoRootFlags(item)} testId={`tree-flags-${item.path}`} />
            {item.heavy && (
              <span className="tree-heavy-count" title="Dependencies or build output: listed only when you open it">{item.childCount ?? ''}</span>
            )}
            {/* A collapsed folder rolls up everyone inside it; an open one leaves it to its rows. */}
            {!(item.type === 'tree' && expandedFolders.has(item.path)) && (
              <TreePresenceMarker folder={item.type === 'tree'} presence={presenceAt(presence, vfsPathFor(item.path), {folder: item.type === 'tree'})} you={presence.you} />
            )}
            {item.type === 'blob' && <ModifiedMarker entry={sinceLastSeen.byPath.get(vfsPathFor(item.path))} you={presence.you} />}
            {itemHasErrors && item.type === 'blob' && (
              <span
                className="fix-ai-btn"
                onClick={(e) => handleAskAIToFix(e, item)}
                title="Ask AI to fix errors"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAskAIToFix(e, item);
                  }
                }}
              >
                <Icon name="sparkles" size={14} />
              </span>
            )}
            {itemHasErrors && (
              <span className="error-indicator" title={item.type === 'blob' ? 'Has errors' : 'Contains files with errors'}>
                <Icon name="warning" size={12} />
              </span>
            )}
            {itemHasModifications && (
              <span className="modified-indicator" title={item.type === 'blob' ? 'Modified (not pushed to GitLab)' : 'Contains modified files'}>
                <Icon name="circle" size={8} />
              </span>
            )}
            {item.deleted && (
              <span className="deleted-badge">
                <Icon name="trash" size={12} />
              </span>
            )}
            {item.isNew && (
              <span className="new-badge">
                <Icon name="plus-circle" size={12} />
              </span>
            )}
          </button>
        );
              })}
            {virtualized && lastRow < rows.length && <div aria-hidden className="tree-rows-spacer" style={{height: (rows.length - lastRow) * ROW_HEIGHT}} />}
              </div>
            </>
          ) : (
            <div className="search-in-files-container">
              <MonacoSearchPanel
                projectId={miaProjectId}
                serviceName={serviceName}
                onFileSelect={(filePath, lineNumber) => {
                  // MonacoSearchPanel passes (filePath, lineNumber) as two params
                  // Convert to file object format that parent expects
                  onFileSelect({
                    path: filePath,
                    name: filePath.split('/').pop(),
                    lineNumber
                  });
                }}
                onClose={() => setActiveTab('files')}
              />
            </div>
          )}
        </div>
      
      {/* Context Menu — rendered in a portal on document.body. Inside the tree it is `position: fixed`, which
          is resolved against the nearest ancestor with a transform/filter/containment instead of the viewport,
          so the menu used to open far away from the item that was clicked. */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="context-menu"
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 9999
          }}
        >
          {contextMenu.kind === 'upload' ? (
            <div className="tree-upload-menu" role="menu">
              <button className="context-menu-item" data-testid="tree-upload-pick-files" onClick={() => pickFiles('files', contextMenu.target)} role="menuitem" type="button">
                <Icon name="file-plus" size={14} />
                <span>Upload files…</span>
              </button>
              <button className="context-menu-item" data-testid="tree-upload-pick-folder" onClick={() => pickFiles('folder', contextMenu.target)} role="menuitem" type="button">
                <Icon name="folder-plus" size={14} />
                <span>Upload folder…</span>
              </button>
              <div className="tree-upload-menu-hint">Into {contextMenu.target || (serviceName && serviceName !== '.' ? serviceName : 'the project root')}</div>
            </div>
          ) : (
            <>
              {contextMenu.item.type === 'tree' && canCreate && (
                <>
                  <button
                    className="context-menu-item"
                    data-testid="tree-menu-new-file"
                    disabled={Boolean(lockedReason(contextMenu.item.path))}
                    onClick={handleCreateFile}
                    title={lockedReason(contextMenu.item.path) || undefined}
                    type="button"
                  >
                    <Icon name="file-plus" size={14} />
                    <span>New file…</span>
                  </button>
                  {canCreateFolder && (
                    <button
                      className="context-menu-item"
                      data-testid="tree-menu-new-folder"
                      disabled={Boolean(lockedReason(contextMenu.item.path))}
                      onClick={handleCreateFolder}
                      title={lockedReason(contextMenu.item.path) || undefined}
                      type="button"
                    >
                      <Icon name="folder-plus" size={14} />
                      <span>New folder…</span>
                    </button>
                  )}
                  {canUpload && !lockedReason(contextMenu.item.path) && (
                    <>
                      <button className="context-menu-item" data-testid="tree-folder-upload-files" onClick={() => pickFiles('files', contextMenu.item.path)} type="button">
                        <Icon name="upload" size={14} />
                        <span>Upload files…</span>
                      </button>
                      <button className="context-menu-item" data-testid="tree-folder-upload-folder" onClick={() => pickFiles('folder', contextMenu.item.path)} type="button">
                        <Icon name="upload" size={14} />
                        <span>Upload folder…</span>
                      </button>
                    </>
                  )}
                  <div className="context-menu-separator" />
                </>
              )}
              {canRelocate(contextMenu.item) && (
                <>
                  <button className="context-menu-item" data-testid="tree-menu-rename" onClick={() => startRename(contextMenu.item)} type="button">
                    <Icon name="edit" size={14} />
                    <span>Rename</span>
                    <kbd className="context-menu-shortcut">F2</kbd>
                  </button>
                  <button
                    className="context-menu-item"
                    data-testid="tree-menu-move"
                    onClick={() => { setMovingItem(contextMenu.item); setContextMenu(null); }}
                    type="button"
                  >
                    <Icon name="folder" size={14} />
                    <span>Move to…</span>
                  </button>
                  <div className="context-menu-separator" />
                </>
              )}
              <button
                className="context-menu-item delete"
                data-testid="tree-menu-delete"
                disabled={Boolean(lockedReason(contextMenu.item.path))}
                onClick={handleDeleteItem}
                title={lockedReason(contextMenu.item.path) || undefined}
                type="button"
              >
                <Icon name="trash" size={14} />
                <span>{contextMenu.item.type === 'tree' ? 'Delete Folder' : 'Delete File'}</span>
              </button>
            </>
          )}
        </div>,
        document.body
      )}
      
      {deleting && (
        <TreeDeleteConfirm
          busy={deleting.busy}
          fileCount={deleting.fileCount}
          item={deleting.item}
          onCancel={() => { pendingFocusRef.current = deleting.item.path; setDeleting(null); }}
          onConfirm={confirmDelete}
          restorable={canMoveItems}
        />
      )}
      {creating && (
        <TreeCreateDialog
          allowNested={creating.kind === 'file' || createsOnVfs}
          entryAt={(name) => entryAtTreePath(childPath(creating.folder, name))}
          kind={creating.kind}
          location={createLocation(creating.folder)}
          onClose={() => setCreating(null)}
          onCreate={(name) => createEntry(creating, name)}
        />
      )}
      {movingItem && (
        <TreeMoveDialog
          fetchFolders={fetchMoveFolders}
          folders={folderChoices}
          isTaken={(path) => tree.some((entry) => entry.path === path && !entry.deleted)}
          item={movingItem}
          onClose={() => { pendingFocusRef.current = movingItem.path; setMovingItem(null); }}
          onMove={async (targetFolder) => {
            setMoveError(null);
            await relocateItem(movingItem.path, childPath(targetFolder, baseName(movingItem.path)));
            setMovingItem(null);
          }}
          rootLabel={serviceName && serviceName !== '.' ? serviceName : ''}
        />
      )}
      {zipChoice && (
        <TreeZipChoice
          collected={zipChoice.collected}
          onClose={() => setZipChoice(null)}
          onKeep={() => { setUploadSession(zipChoice); setZipChoice(null); }}
          onUnpacked={(collected) => { setUploadSession({collected, target: zipChoice.target}); setZipChoice(null); }}
        />
      )}
      {uploadSession && (
        <TreeUploadDialog
          collected={uploadSession.collected}
          folderChoices={folderChoices}
          onClose={() => setUploadSession(null)}
          onFinished={handleUploadFinished}
          projectId={miaProjectId}
          rootLabel={serviceName && serviceName !== '.' ? serviceName : ''}
          target={uploadSession.target}
          toVfsPath={(treePath) => (treePath ? vfsPathFor(treePath) : vfsPathFor(''))}
        />
      )}

      {/* Problems Modal */}
      <Modal
        isOpen={showProblemsModal}
        onClose={() => setShowProblemsModal(false)}
        title="Files with Problems"
        type="custom"
        className="problems-modal"
      >
        <div className="problems-modal-content">
          {getFilesWithProblems().length === 0 ? (
            <div className="no-problems">
              <Icon name="check-circle" size={32} />
              <p>No problems detected in any files</p>
            </div>
          ) : (
            <div className="problems-list">
              <div className="problems-summary">
                <Icon name="warning" size={20} />
                <span>{getFilesWithProblems().length} file(s) with problems</span>
              </div>
              {getFilesWithProblems().map(({filePath, errors, errorCount}) => (
                <div key={filePath} className="problem-file">
                  <div className="problem-file-header" onClick={() => onFileSelect(filePath)}>
                    <Icon name={getFileIconName(filePath)} size={16} />
                    <span className="problem-file-path">{filePath}</span>
                    <span className="problem-count">{errorCount} problem(s)</span>
                  </div>
                  <div className="problem-errors">
                    {errors.map((error, idx) => (
                      <div key={idx} className="problem-error">
                        <span className="problem-line">Line {error.startLineNumber}</span>
                        <span className="problem-message">{error.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
    </>
  );
};

RepositoryTree.propTypes = {
  fileErrors: PropTypes.object,
  onAskAIToFix: PropTypes.func,
  onFileSelect: PropTypes.func,
  projectId: PropTypes.string,
  repositoryUrl: PropTypes.string,
  selectedFilePath: PropTypes.string,
  serviceName: PropTypes.string,
  isPullingFromGit: PropTypes.bool,
  tenantId: PropTypes.string,
  branchName: PropTypes.string,
  vfsBacked: PropTypes.bool,
  autoOpenStrict: PropTypes.bool,
  onAutoOpenMissing: PropTypes.func,
  readOnly: PropTypes.bool
};

export default RepositoryTree;
